/**
 * PlanHomeScreen
 *
 * Signal-driven, plan-based homepage.
 * Replaces the legacy onboarding-derived HomeScreen with a
 * stepper-based plan unique to each user's journey.
 * Supports both primary and partner views.
 */

import { getTodayString, toLocalDateString } from "@src/packages/utils/date";
import { appointmentKeys } from "@src/packages/care/patient-cache-keys";
import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { View, ScrollView } from "react-native";
import {
  CommonActions,
  useNavigation,
  type NavigationProp,
} from "@react-navigation/native";
import { CalendarClock, BookOpen } from "lucide-react-native";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useAppointments } from "@src/packages/care/hooks/useAppointments";
import { useUpdateAppointment } from "@src/packages/care/useCalendarData";
import {
  useActiveCycleWithPhases,
  useCreateCycleProcedureEvent,
  useCycleProcedureEvents,
  useCycles,
} from "@src/packages/cycles/hooks/useCycles";
import { hasConfirmedPregnancyProcedure } from "@src/packages/cycles/ivfFlowGuards";
import {
  DEV_ACTIVE_CYCLE_OVERRIDE,
  DEV_ACTIVE_CYCLE_SCENARIO,
} from "@src/packages/cycles/dev/devActiveCycleOverride";
import { devTodayTreatment } from "@src/packages/cycles/dev/devTodayTreatment";
import { getPostProcedureWaitContext } from "@src/packages/cycles/presenters/postProcedureWait";
import { isLiningPreparationCycleType } from "@src/packages/cycles/monitoredCycleTypes";
import { useCycleHeaderState } from "@src/packages/cycles/hooks/useCycleHeaderState";
import { ivfHomeStep } from "@src/packages/cycles/presenters/ivfHomeStep";
import {
  isAwaitingSemenAnalysis,
  isSpermSampleCollected,
} from "@src/packages/cycles/presenters/semenAnalysisWait";
import {
  getPostProcedureOutcomeContext,
  type PostProcedureOutcomeResultStatus,
} from "@src/packages/cycles/presenters/postProcedureOutcome";
import {
  isPostProcedureTransitionTimeReached,
  nextDayForPostProcedure,
  resolvePostProcedureTransitionCandidate,
  rescheduledProcedureEnd,
  serializePostProcedureTransitionRecord,
  shouldAutoPromptPostProcedureTransition,
  type PostProcedureTransitionCandidate,
  type PostProcedureEventKind,
  type PostProcedureTransitionStatus,
} from "@src/packages/cycles/presenters/postProcedureTransition";
import {
  ActionRowCard,
  BackLink,
  CardSurface,
  Dialog,
  DialogHeader,
  EmptyState,
  LoadingState,
} from "@louise/moon-ui";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../App";
import type { Patient } from "@src/packages/patients/patients.types";
import type {
  CareStackParamList,
  AccessStackParamList,
  CostsStackParamList,
  ChatStackParamList,
  MainTabParamList,
} from "../types";
import { useDocumentUploadController } from "../DocumentUploadController";
import { useStartTreatmentController } from "../StartTreatmentController";
import { useLocale, useRegion, useTranslations } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { TranslationFunction } from "@src/packages/i18n";
import ScreenHeader from "../components/ScreenHeader";
import RoleAdaptedHomeBanner from "../components/RoleAdaptedHomeBanner";
import HelperJourneyHero from "../components/HelperJourneyHero";
import HelperHomeActionCard from "../components/HelperHomeActionCard";
import HelperJourneyStep from "../components/HelperJourneyStep";
import CompensationPlaceholderCard from "../components/CompensationPlaceholderCard";
import { resolveCycleDetailAction } from "@src/packages/homepage/plan/resolveCycleDetailAction";
import { semenAnalysisResultsFromSpermiograms } from "@src/packages/homepage/plan/semenAnalysisFromSpermiograms";
import {
  recordedBirthHandoffCopyKeys,
  resolveRecordedBirth,
} from "@src/packages/homepage/plan/recordedBirth";
import { shouldShowEarlyPregnancyGuidance } from "@src/packages/homepage/plan/showEarlyPregnancyGuidance";
import { CarrierGuidedHome } from "@src/packages/homepage/plan/components/CarrierGuidedHome";
import type { CarrierJourneyScenario } from "@src/packages/homepage/plan/components/carrierJourney";
import { DEV_HELPER_ROLE_OVERRIDE } from "../components/devHelperRoleOverride";
import MedicalHistoryStepCard from "@src/packages/homepage/plan/components/MedicalHistoryStepCard";
import type { HeroCycleEnding } from "@src/packages/homepage/plan/components/hero/Hero";
import { PreCycleEggFreezingHome } from "@src/packages/homepage/plan/components/PreCycleEggFreezingHome";
import { PreCycleEmbryoFreezingHome } from "@src/packages/homepage/plan/components/PreCycleEmbryoFreezingHome";
import { PreCycleSpermFreezingHome } from "@src/packages/homepage/plan/components/PreCycleSpermFreezingHome";
import {
  GuidedPreCycleConceptHome,
  type GuidedPreCycleConceptVariant,
} from "@src/packages/homepage/plan/components/GuidedPreCycleConceptHome";
import {
  HomepageReviewConceptHome,
  getPostProcedureOutcomeContentKey,
  getHomepageReviewConceptVariant,
  isPostProcedureOutcomeVariant,
  type CycleStatusReviewKind,
  type HomepageReviewConceptVariant,
  type HomepageMedicationFactSheetPayload,
  type HomepageProcedureFactSheetPayload,
  type HomepageTestFactSheetPayload,
  type TodayRow,
  type PeekAheadDay,
} from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { buildTodayRowsByDate } from "./care/suggested/conceptCalendarData";
import MedicationFactsheetSheet from "../components/MedicationFactsheetSheet";
import { AddMedicationModal } from "./care/modals/AddMedicationModal";
import type { EditMedicationData } from "./care/modals/AddMedicationModal";
import ProcedureDetailSheet from "../components/ProcedureDetailSheet";
import TestDetailSheet from "../components/TestDetailSheet";
import OvarianStimulationMonitoringSection from "./care/sections/OvarianStimulationMonitoringSection";
import RecordsSection, {
  RECORDS_CATEGORY_TO_DOC_CATEGORY,
  type RecordsCategory,
} from "./care/sections/RecordsSection";
import {
  useUserPersona,
  type PersonaHelperRole,
} from "@src/packages/persona/useUserPersona";
import {
  resolveHomeExperience,
  usesExploreHomeScenario,
  type HomeExperience,
} from "@src/packages/homepage/experience";
import { isPostRetrievalPhase } from "@src/packages/homepage/experience/isPostRetrievalPhase";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP, HEADER_INSET } from "@louise/moon-ui/theme/tokens";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import {
  buildFertilityMetrics,
  type FertilityMetricWrite,
  useCreatePhase,
  useUpdatePhase,
} from "@src/packages/patients/hooks/usePhases";
import {
  useCreatePatientNote,
  usePatientNotes,
} from "@src/packages/care/hooks/usePatientNotes";
import {
  useHomepageCards,
  FETParticipantHomeView,
  useTestResultInsights,
  getJourneyStepCompletionId,
} from "@src/packages/homepage/plan";
import { EGG_DONATION_GUIDE_ARTICLE_ID } from "@src/packages/homepage/plan/journeys";
import TestResultInsightCardWithDetail from "../components/TestResultInsightCardWithDetail";
import {
  useTreatmentDashboard,
  type UseTreatmentDashboardResult,
} from "@src/packages/homepage/plan/hooks/useTreatmentDashboard";
import { useClosedCycleCryoStorage } from "@src/packages/homepage/plan/hooks/useClosedCycleCryoStorage";
import { useFETParticipantViews } from "@src/packages/cycles/useFETParticipantViews";
import { useFETPlanForCycle } from "@src/packages/cycles/useFETPlan";
import {
  FETProcedureReportUnavailableError,
  useRecordFETProcedureReport,
  useReportFETTransferSelf,
} from "@src/packages/cycles/useFETProcedure";
import {
  resolveCycleClosingContent,
  resolveCycleClosingRetired,
} from "@src/packages/homepage/closing/cycleClosingSlides";
import {
  hasConcludedPregnancy,
  isCycleClosingArrival,
  isCycleEndingRecent,
  homeSubjectClosedCycle,
  NO_CYCLE_ENDING,
  readCycleEnding,
  shouldUseCycleEndingForHomeExperience,
} from "@src/packages/homepage/experience/selectHomeCycleEnding";
import { useCreateTreatmentSnapshot } from "@src/packages/cycles/hooks/usePastCycleDetail";
import {
  useCyclePregnancyFollowUp,
  useCyclePregnancyStatuses,
} from "@src/packages/homepage/pregnancy";
import { OutcomeFunnelModal } from "@src/packages/homepage/plan/components/OutcomeFunnelModal";
import {
  createIdempotencyKey,
  useFrozenSpecimens,
} from "@src/packages/care/cryovault/useSpecimens";
import { DEV_HOMEPAGE_ARCHIVE } from "@src/packages/homepage/plan/dev/devCardPreview";
import { HomepageComponentArchive } from "@src/packages/homepage/plan/dev/HomepageComponentArchive";
import type {
  PlanAction,
  Pathway,
  ResultInterpretation,
} from "@src/packages/homepage/plan";
import type {
  HomepageGender,
  HomepageGoal,
} from "@src/packages/homepage/homepage.types";
import { resolvePlannedModality } from "@src/packages/homepage/plan/resolvePlannedModality";
import { resolveAcceptedPlanModality } from "@src/packages/homepage/plan/resolveAcceptedPlanModality";
import { useTreatmentPlan } from "@src/packages/homepage/plan/hooks/useTreatmentPlan";
import { useManualStepCompletion } from "@src/packages/homepage/plan/hooks/useManualStepCompletion";
import RoadmapModal, {
  resolveJourneyPath,
  resolveRoadmapPhaseId,
} from "./YouScreen/modals/RoadmapModal";
import { useJourneySignals } from "@src/packages/cycles/hooks/useJourneySignals";
import { selectJourneyCycle } from "@src/packages/cycles/presenters/journeyPhase";
import InsuranceInfoModal from "./YouScreen/modals/InsuranceInfoModal";
import IntentPickerModal, {
  type IntentPickerStage,
  type IntentPickerPrimaryIntent,
} from "./YouScreen/modals/IntentPickerModal";
import { useSaveIntentChoice } from "./YouScreen/modals/useSaveIntentChoice";
import EndCycleModal from "./YouScreen/modals/EndCycleModal";
import PartnerInviteModal from "./YouScreen/modals/PartnerInviteModal";
import { useSendPartnerInvite } from "@src/packages/patients/hooks/usePartnerInvite";
import { usePartnerSharingScope } from "@src/packages/patients/hooks/usePartnerSharingScope";
import type { SharingCategory } from "@src/packages/patients/hooks/usePatientSharing";
import type { CycleType as StartTreatmentCycleType } from "./care/modals/MoonStartTreatmentModal";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { JourneyHandoffCard } from "@src/packages/homepage/plan/components/JourneyHandoffCard";
import { PregnancyTrackingSetupDialog } from "@src/packages/homepage/plan/components/PregnancyTrackingSetupDialog";
import { OrganicExplanatoryIllustration } from "@src/packages/homepage/plan/components/EggFreezingBiologyDeepCarouselCard";
import { AiAssistantCard } from "../components/AiAssistantCard";
import {
  PostProcedureTransitionDialog,
  type PostProcedureTransitionResult,
} from "@src/packages/homepage/plan/components/PostProcedureTransitionDialog";
import ArticleSectionCard from "../components/ArticleSectionCard";
import ArticleBannerCard from "../components/ArticleBannerCard";
import { getWaitStageReadings } from "../content/waitStageReadings";
import { getStimStageReadings } from "../content/stimStageReadings";
import { useReadArticles } from "@louise/favorites";
import { useAuthGate } from "../AuthGateContext";
import { useSharedValue, withSpring } from "react-native-reanimated";
import {
  resolveActiveViewerRole,
  isCurrentPartnerParticipation,
  participantRoleToViewerRole,
  readExperienceDevOverrides,
} from "@src/packages/experience";
import {
  PRESERVATION_CYCLE_TYPES,
  resolvePostCycleLifecycle,
} from "@src/packages/cycles/presenters/postCycleState";
import { resolvePostCycleHandoffJourney } from "@src/packages/homepage/plan/postCycleHandoff";
import { resolvePostCycleStorageVariant } from "@src/packages/homepage/plan/resolvePostCycleStorageVariant";
import { useApiClient } from "@src/packages/api/louise-api";
import { useIsAuthReady } from "@src/packages/api/useStytch";
import { resolveAuthorizedProjectionStatus } from "@src/packages/experience/resolveAuthorizedProjectionStatus";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";

const clinicsIcon =
  require("../../assets/images/you-icons/clinics.png") as number;
const learnIcon = require("../../assets/images/you-icons/learn.png") as number;
const handoffBackground =
  require("../../assets/images/silk-brown.png") as number;

function buildPendingSemenAnalysisInsight(
  t: TranslationFunction,
): ResultInterpretation {
  return {
    testName: t("homepage.pending_semen_analysis.test_name"),
    testPurpose: t("homepage.pending_semen_analysis.test_purpose"),
    value: t("homepage.pending_semen_analysis.value"),
    unit: "",
    // No result yet, so nothing to place on the bar.
    category: null,
    headline: t("homepage.pending_semen_analysis.headline"),
    interpretation: t("homepage.pending_semen_analysis.interpretation"),
    whatThisMeans: t("homepage.pending_semen_analysis.what_this_means"),
    nextStep: t("homepage.pending_semen_analysis.next_step"),
  };
}

function getDevSearch(): string | undefined {
  if (!__DEV__ || typeof window === "undefined") return undefined;
  const search = window.location.search;
  if (!search) return undefined;

  try {
    const hasDevParameter = [...new URLSearchParams(search).keys()].some(
      (key) => key.startsWith("dev_") || key.startsWith("force_"),
    );
    return hasDevParameter ? search : undefined;
  } catch {
    return undefined;
  }
}

function getDevSuggestedHomeSurface(): string | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("dev_home_suggested") ??
      params.get("dev_home_suggested_surface")
    );
  } catch {
    return null;
  }
}

function getDevOpenOverlay(): "outcome-funnel" | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const overlay = params.get("dev_open") ?? params.get("dev_open_overlay");
    return overlay === "outcome-funnel" ? overlay : null;
  } catch {
    return null;
  }
}

function getDevPostProcedurePrompt():
  "transfer" | "insemination" | "oi" | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const value = new URLSearchParams(window.location.search).get(
      "dev_post_procedure",
    );
    return value === "transfer" || value === "insemination" || value === "oi"
      ? value
      : null;
  } catch {
    return null;
  }
}

function stableProcedureEventRequestId(seed: string): string {
  let hash = 2166136261;
  for (const character of seed) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  const first = (hash >>> 0).toString(16).padStart(8, "0");
  const second = (Math.imul(hash, 31) >>> 0).toString(16).padStart(8, "0");
  const third = (Math.imul(hash, 131) >>> 0).toString(16).padStart(8, "0");
  const fourth = (Math.imul(hash, 257) >>> 0).toString(16).padStart(8, "0");
  const hex = `${first}${second}${third}${fourth}`.slice(0, 32);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-${(
    (Number.parseInt(hex.slice(16, 18), 16) & 0x3f) |
    0x80
  )
    .toString(16)
    .padStart(2, "0")}${hex.slice(18, 20)}-${hex.slice(20, 32)}`;
}

function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function procedureEventDateTime(date: string, time: string | null): string {
  return `${date}T${time ?? "12:00"}:00`;
}

function getDevPatientMedicationActionsEnabled(): boolean {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        "dev_patient_medication_actions",
      ) === "true"
    );
  } catch {
    return false;
  }
}

function getDevPatientMedicationActionsMissingIdEnabled(): boolean {
  if (!getDevPatientMedicationActionsEnabled()) return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        "dev_medication_missing_id",
      ) === "true"
    );
  } catch {
    return false;
  }
}

function getAutoHomepageReviewConceptVariant({
  homeExperience,
  treatmentData,
  endedCycleType,
}: {
  homeExperience: HomeExperience;
  treatmentData: UseTreatmentDashboardResult;
  /**
   * The type of the closed cycle this Home is about. Passed in because a
   * preservation cycle carries no outcome, so it never reaches the experience's
   * own `treatment.cycleType` — see `resolvePostCycleStorageVariant`.
   */
  endedCycleType?: string | null;
}): HomepageReviewConceptVariant | null {
  const contextCycleType = homeExperience.context.treatment.cycleType;
  const cycleType =
    contextCycleType !== "unknown"
      ? contextCycleType
      : (treatmentData.rawCycleType ?? "unknown");
  const treatmentGoal = homeExperience.context.familyBuildingPlan.treatmentGoal;
  const primaryIntent = homeExperience.context.familyBuildingPlan.primaryIntent;
  const sexAtBirth = homeExperience.context.viewer.sexAtBirth;

  if (usesExploreHomeScenario(homeExperience.scenario)) {
    return "learn-explorer";
  }
  if (homeExperience.scenario === "trying_naturally") {
    return "trying-naturally";
  }
  if (homeExperience.scenario === "intended_parent_matching") {
    return "intended-parent";
  }
  // Carrier journeys have their own guided helper surface. Keep them out of
  // the donor concept variants, even when the carrier's primary intent is
  // derived as "donate" from the persona model.
  if (
    homeExperience.scenario === "carrier_application" ||
    homeExperience.scenario === "carrier_screening" ||
    homeExperience.scenario === "carrier_matched_legal"
  ) {
    return null;
  }
  // A donor in an active cycle reuses the preservation active home: an egg
  // donor's retrieval cycle mirrors egg freezing, a sperm donor's collection
  // mirrors sperm freezing. This must precede the generic donate check below,
  // which otherwise pins every donor to the static application view.
  if (homeExperience.scenario === "donor_active_cycle") {
    return sexAtBirth === "AMAB"
      ? "sperm-freezing-active"
      : "egg-freezing-active";
  }
  if (primaryIntent === "donate") {
    if (treatmentGoal === "sperm-donation" || sexAtBirth === "AMAB") {
      return "sperm-donor";
    }
    return "egg-donor";
  }
  if (homeExperience.scenario === "planning_iui") return "iui-precycle";
  if (homeExperience.scenario === "planning_ivf") return "ivf-precycle";
  if (homeExperience.scenario === "waiting_semen_results") {
    return "sperm-freezing-wait";
  }
  if (homeExperience.scenario === "waiting_beta") {
    const waitContext = getPostProcedureWaitContext(cycleType);
    if (waitContext.procedure === "ovulation_window") return "oi-wait";
    return waitContext.procedure === "insemination" ? "iui-wait" : "beta-wait";
  }
  if (homeExperience.scenario === "results_missing") {
    if (treatmentGoal === "sperm-freezing" || cycleType === "sperm_freezing") {
      return "sperm-freezing-wait";
    }
    if (
      cycleType === "ivf" ||
      cycleType === "icsi" ||
      cycleType === "fet" ||
      cycleType === "iui" ||
      cycleType === "oi"
    ) {
      return getPostProcedureOutcomeContext(cycleType, "unavailable").route;
    }
    const waitContext = getPostProcedureWaitContext(cycleType);
    if (waitContext.procedure === "ovulation_window") return "oi-wait";
    return waitContext.procedure === "insemination" ? "iui-wait" : "beta-wait";
  }
  if (
    homeExperience.scenario === "results_positive" ||
    homeExperience.scenario === "results_negative" ||
    homeExperience.scenario === "results_uncertain" ||
    homeExperience.scenario === "results_adverse"
  ) {
    const resultStatus: PostProcedureOutcomeResultStatus =
      homeExperience.scenario === "results_positive"
        ? "verified-positive"
        : homeExperience.scenario === "results_negative"
          ? "verified-negative"
          : homeExperience.scenario === "results_uncertain"
            ? "uncertain"
            : "adverse";
    return getPostProcedureOutcomeContext(cycleType, resultStatus).route;
  }
  if (
    homeExperience.scenario === "results_cancelled" ||
    homeExperience.scenario === "results_converted"
  ) {
    return "cycle-status-review";
  }
  if (homeExperience.scenario === "postcycle_storage") {
    return resolvePostCycleStorageVariant({
      endedCycleType,
      cycleType,
      treatmentGoal,
    });
  }

  if (homeExperience.surface === "active-treatment") {
    const activeCycleVariant = resolveActiveCycleVariant({
      homeExperience,
      treatmentData,
      cycleType,
      treatmentGoal,
    });
    if (activeCycleVariant) return activeCycleVariant;
  }

  return null;
}

/**
 * The active-cycle layout for an open cycle, read from its type and phase.
 *
 * Its own function because two roads reach an open cycle: the active-treatment
 * surface, and the fallback below for an open cycle that surface does not
 * claim. Both have to land on the same layout.
 */
function resolveActiveCycleVariant({
  homeExperience,
  treatmentData,
  cycleType,
  treatmentGoal,
}: {
  homeExperience: HomeExperience;
  treatmentData: UseTreatmentDashboardResult;
  cycleType:
    | HomeExperience["context"]["treatment"]["cycleType"]
    | NonNullable<UseTreatmentDashboardResult["rawCycleType"]>;
  treatmentGoal: HomeExperience["context"]["familyBuildingPlan"]["treatmentGoal"];
}): HomepageReviewConceptVariant | null {
  if (
    isPostRetrievalPhase({
      cycleType: cycleType === "unknown" ? undefined : cycleType,
      currentPhase: treatmentData.rawCurrentPhase,
      isInTreatment: treatmentData.isInTreatment,
      retrievalStillAhead: treatmentData.retrievalStillAhead,
    })
  ) {
    return "post-retrieval";
  }
  if (treatmentGoal === "egg-freezing" || cycleType === "egg_freezing") {
    return "egg-freezing-active";
  }
  if (treatmentGoal === "embryo-freezing" || cycleType === "embryo_freezing") {
    return "embryo-freezing-active";
  }
  if (treatmentGoal === "sperm-freezing" || cycleType === "sperm_freezing") {
    return "sperm-freezing-active";
  }
  if (cycleType === "iui") return "iui-active";
  if (cycleType === "fet") return "fet-active";
  if (cycleType === "ivf" || cycleType === "icsi") return "ivf-active";
  if (cycleType === "oi") return "oi-active";

  /**
   * A cycle in a stimulation phase is a stimulated cycle, whatever type it
   * was recorded as, or whether it was recorded at all.
   *
   * Last of the active-treatment arms on purpose: every type with a home of
   * its own has already claimed it above, so this only catches what is left.
   * Before it, an ovulation-induction cycle and a cycle with no type reached
   * `return null` at the bottom of this function, and returning null does
   * not merely drop the hero card: the whole review-concept home is gated on
   * this variant, so the day's sections and the monitoring card went with it.
   * The screen kept its scaffolding, which is why it read as a homepage that
   * had lost its hero rather than as a homepage that had resolved to nothing.
   *
   * The scenario layer already names this state: `resolveExperienceScenario`
   * maps the protocol and stimulation phases to `active_ovarian_stimulation`.
   * The router simply never asked it, only the cycle's type.
   */
  if (homeExperience.scenario === "active_ovarian_stimulation") {
    return "oi-active";
  }

  return null;
}

/**
 * The layout for a Home that nothing more specific claims.
 *
 * These states used to fall through to the old card stack, which is gone, so
 * each gets an existing layout instead of an empty page:
 *
 * - An open cycle the active-treatment surface does not claim, such as a
 *   carrier mid-transfer, who stays on the helper surface by design. It gets
 *   the active-cycle layout for the cycle it is in, which is what the old
 *   stack's in-treatment cards were standing in for.
 * - Anything else with no layout of its own and no cycle to show, such as a
 *   "stimulation" stage recorded before any cycle exists. It gets the explorer
 *   layout, which is honest about what we know.
 *
 * Donors and carriers outside a cycle keep their helper home, and the guided
 * pre-cycle surfaces render their own, so neither gets a fallback here.
 */
function getFallbackHomepageReviewConceptVariant({
  homeExperience,
  treatmentData,
  rendersPreCycleSurface,
  hasHelperHome,
}: {
  homeExperience: HomeExperience;
  treatmentData: UseTreatmentDashboardResult;
  rendersPreCycleSurface: boolean;
  hasHelperHome: boolean;
}): HomepageReviewConceptVariant | null {
  if (rendersPreCycleSurface) return null;
  if (treatmentData.isInTreatment) {
    const contextCycleType = homeExperience.context.treatment.cycleType;
    const activeCycleVariant = resolveActiveCycleVariant({
      homeExperience,
      treatmentData,
      cycleType:
        contextCycleType !== "unknown"
          ? contextCycleType
          : (treatmentData.rawCycleType ?? "unknown"),
      treatmentGoal: homeExperience.context.familyBuildingPlan.treatmentGoal,
    });
    if (activeCycleVariant) return activeCycleVariant;
  }
  return hasHelperHome ? null : "learn-explorer";
}

function getGuidedPreCycleConceptVariant(
  variant: HomepageReviewConceptVariant | null,
): GuidedPreCycleConceptVariant | null {
  switch (variant) {
    case "setup":
    case "learn-explorer":
    case "trying-naturally":
    case "ivf-precycle":
    case "iui-precycle":
    case "amab-family-building-precycle":
      return variant;
    default:
      return null;
  }
}

function shouldShowPendingSemenAnalysisCard({
  homeExperience,
  treatmentData,
  testInsights,
}: {
  homeExperience: HomeExperience;
  treatmentData: UseTreatmentDashboardResult;
  testInsights: ResultInterpretation[];
}): boolean {
  const treatmentGoal = homeExperience.context.familyBuildingPlan.treatmentGoal;
  const cycleType =
    homeExperience.context.treatment.cycleType !== "unknown"
      ? homeExperience.context.treatment.cycleType
      : (treatmentData.rawCycleType ?? "unknown");
  const isSpermFreezing =
    treatmentGoal === "sperm-freezing" || cycleType === "sperm_freezing";
  const isPendingSemenScenario =
    homeExperience.scenario === "waiting_semen_results" ||
    (homeExperience.scenario === "results_missing" && isSpermFreezing);
  // On an open cycle the wait Home also covers the analysis and freezing steps,
  // so the scenario alone cannot say the analysis is still pending. The steps
  // can: a value entered on the analysis step ends the wait.
  const isAnalysisPending = homeExperience.context.treatment.hasActiveCycle
    ? homeExperience.context.treatment.awaitingSemenAnalysis
    : true;
  const hasSemenResult = testInsights.some(
    (insight) => insight.test === "semen_analysis",
  );

  return (
    isSpermFreezing &&
    isPendingSemenScenario &&
    isAnalysisPending &&
    !homeExperience.context.metrics.hasSemenSummary &&
    !hasSemenResult
  );
}

type HelperUpcomingStep = {
  title: string;
  subtitle: string;
  // When present the step renders as a pressable card (icon + chevron)
  // and navigates on press. When absent the step is a read-only preview.
  action?: { imageSource: number; screen: string };
};

// Scaffold steps below the active Medical History step on the donor / carrier
// home. These are read-only previews of the journey to reduce overwhelm —
// real product work on agencies, screening flow, contract upload, and cycle
// telemetry is tracked under task 1.5 part 5 in EXECUTION_PLAN.md.
function getHelperUpcomingSteps(
  t: TranslationFunction,
): Record<NonNullable<PersonaHelperRole>, HelperUpcomingStep[]> {
  return {
    donor: [
      {
        title: t("home.helper_steps.donor.apply_title"),
        subtitle: t("home.helper_steps.donor.apply_subtitle"),
        action: { imageSource: clinicsIcon, screen: "FindCareScreen" },
      },
      {
        title: t("home.helper_steps.donor.screened_title"),
        subtitle: t("home.helper_steps.donor.screened_subtitle"),
      },
      {
        title: t("home.helper_steps.donor.match_title"),
        subtitle: t("home.helper_steps.donor.match_subtitle"),
      },
      {
        title: t("home.helper_steps.donor.sign_title"),
        subtitle: t("home.helper_steps.donor.sign_subtitle"),
      },
      {
        title: t("home.helper_steps.donor.begin_title"),
        subtitle: t("home.helper_steps.donor.begin_subtitle"),
      },
    ],
    carrier: [
      {
        title: t("home.helper_steps.carrier.apply_title"),
        subtitle: t("home.helper_steps.carrier.apply_subtitle"),
        action: { imageSource: clinicsIcon, screen: "FindCareScreen" },
      },
      {
        title: t("home.helper_steps.carrier.screened_title"),
        subtitle: t("home.helper_steps.carrier.screened_subtitle"),
      },
      {
        title: t("home.helper_steps.carrier.match_title"),
        subtitle: t("home.helper_steps.carrier.match_subtitle"),
      },
      {
        title: t("home.helper_steps.carrier.sign_title"),
        subtitle: t("home.helper_steps.carrier.sign_subtitle"),
      },
      {
        title: t("home.helper_steps.carrier.begin_title"),
        subtitle: t("home.helper_steps.carrier.begin_subtitle"),
      },
    ],
    "intended-parent": [],
  };
}

/**
 * Derive pathway from patient data.
 * Currently checks onboarding partnerChoice; falls back to "solo".
 */
function derivePathway(patient: Patient | undefined): Pathway {
  const partnerChoice = patient?.partnerChoice as string | null | undefined;
  if (partnerChoice === "with-partner") return "with-partner";
  if (partnerChoice === "using-donor") return "with-donor";
  if (partnerChoice === "with-surrogate") return "with-surrogate";
  return "solo";
}

/**
 * The body whose journey the guided plan follows.
 *
 * The viewer's own sex, except for a partner on a preservation plan. Egg
 * freezing is the ovarian journey and sperm freezing the sperm one whoever is
 * reading it, so a partner of the other sex would otherwise be handed the
 * other journey's steps under the right hero.
 */
function resolveHomepageJourneyGender(
  sexAtBirth: HomeExperience["context"]["viewer"]["sexAtBirth"],
  treatmentGoal?: HomeExperience["context"]["familyBuildingPlan"]["treatmentGoal"],
  viewerIsSupporting = false,
): HomepageGender | null {
  if (viewerIsSupporting) {
    if (treatmentGoal === "egg-freezing" || treatmentGoal === "egg-preserve") {
      return "female";
    }
    if (
      treatmentGoal === "sperm-freezing" ||
      treatmentGoal === "sperm-preserve"
    ) {
      return "male";
    }
  }
  if (sexAtBirth === "AFAB") return "female";
  if (sexAtBirth === "AMAB") return "male";
  return null;
}

function resolveHomepageJourneyGoal(
  treatmentGoal: HomeExperience["context"]["familyBuildingPlan"]["treatmentGoal"],
): HomepageGoal | null {
  switch (treatmentGoal) {
    case "egg-freezing":
    case "egg-preserve":
    case "sperm-freezing":
    case "sperm-preserve":
      return "preserve";
    case "embryo-freezing":
    case "embryo-preserve":
      return "embryo-preserve";
    case "pregnancy":
    case "ivf":
    case "iui":
    case "fet":
      return "pregnancy";
    case "egg-donation":
      return "egg-donation";
    case "sperm-donation":
      return "sperm-donation";
    case "surrogacy":
      return "surrogacy";
    case "adoption":
      return "adoption";
    case "exploring":
    case "unknown":
      return null;
    default: {
      const exhaustive: never = treatmentGoal;
      return exhaustive;
    }
  }
}

function isPartnerRole(patient: Patient | undefined): boolean {
  return isCurrentPartnerParticipation(
    patient?.participantRole,
    patient?.participantStatus,
  );
}

/**
 * Calculate user age from date of birth
 */
function calculateAge(dob: string | undefined): number | undefined {
  if (!dob) return undefined;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
}

/**
 * Get time-appropriate greeting
 */
function getGreeting(t: TranslationFunction, firstName?: string): string {
  const hour = new Date().getHours();
  const name = firstName ?? "";
  if (hour < 12) {
    return name
      ? t("home.greeting_morning_named", { params: { name } })
      : t("home.greeting_morning");
  }
  if (hour < 17) {
    return name
      ? t("home.greeting_afternoon_named", { params: { name } })
      : t("home.greeting_afternoon");
  }
  return name
    ? t("home.greeting_evening_named", { params: { name } })
    : t("home.greeting_evening");
}

/**
 * Navigation routing for plan actions.
 * Maps action screen identifiers to their tab + nested screen.
 */
/**
 * Extract screen names that accept undefined (no required params).
 */
type ScreensWithNoRequiredParams<T> = {
  [K in keyof T]: undefined extends T[K] ? K : never;
}[keyof T];

type NestedRoute =
  | { tab: "Care"; screen: ScreensWithNoRequiredParams<CareStackParamList> }
  | { tab: "Access"; screen: ScreensWithNoRequiredParams<AccessStackParamList> }
  | { tab: "Costs"; screen: ScreensWithNoRequiredParams<CostsStackParamList> }
  | { tab: "Learn"; screen: ScreensWithNoRequiredParams<ChatStackParamList> };

const SCREEN_MAP: Record<string, NestedRoute> = {
  AtHomeFertilityTesting: { tab: "Access", screen: "Testing" },
  FindCareScreen: { tab: "Access", screen: "FindCare" },
  DonorRegistryScreen: { tab: "Access", screen: "DonorRegistry" },
  DocumentsScreen: { tab: "Care", screen: "Documents" },
  MedicalHistory: { tab: "Care", screen: "MedicalHistory" },
  MenstrualHealthFlow: { tab: "Care", screen: "MenstrualHealthFlow" },
  CoverageFlow: { tab: "Costs", screen: "Insurance" },
  SupplementsScreen: { tab: "Access", screen: "Supplements" },
  ProgramsScreen: { tab: "Access", screen: "Programs" },
  CostsOverviewScreen: { tab: "Costs", screen: "CostsOverview" },
  GrantsScreen: { tab: "Costs", screen: "Grants" },
  PharmacyScreen: { tab: "Access", screen: "Pharmacy" },
  CycleDetailScreen: { tab: "Care", screen: "CycleDetail" },
  CycleDetail: { tab: "Care", screen: "CycleDetail" },
  CryoVaultScreen: { tab: "Care", screen: "CryoVault" },
  AllPastCycles: { tab: "Care", screen: "AllPastCycles" },
  LearnScreen: { tab: "Learn", screen: "Learn" },
  CareOverview: { tab: "Care", screen: "CareOverview" },
};

type PartnerOrDonorModalStep = "source" | "donor-status";

/** Size of the choice illustrations: they fill the action row's leading slot. */
const CHOICE_ICON_SIZE = 38;

function PartnerOrDonorModal({
  visible,
  step,
  onClose,
  onBack,
  onChoosePartner,
  onChooseDonor,
  onDonorAlreadySelected,
  onNeedDonorHelp,
}: {
  visible: boolean;
  step: PartnerOrDonorModalStep;
  onClose: () => void;
  onBack: () => void;
  onChoosePartner: () => void;
  onChooseDonor: () => void;
  onDonorAlreadySelected: () => void;
  onNeedDonorHelp: () => void;
}) {
  const t = useTranslations();
  const isDonorStep = step === "donor-status";
  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      closeAccessibilityLabel={t("learn.article_actions.close_accessibility")}
      closeAction="close_sheet"
      backdropPressLabel="dismiss_sheet"
      testID="partner-or-donor-dialog"
    >
      {isDonorStep ? (
        <BackLink
          label={t("home.back")}
          action="home.back"
          onPress={onBack}
          style={{ marginBottom: 10 }}
        />
      ) : null}
      <DialogHeader
        title={
          isDonorStep
            ? t("home.donor_modal.title_donor_status")
            : t("home.donor_modal.title_source")
        }
        style={{ marginBottom: 18 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flexShrink: 1 }}
        contentContainerStyle={{ gap: 10 }}
      >
        {isDonorStep ? (
          <>
            <ActionRowCard
              leading={<OrganicMiniIcon kind="donor" size={CHOICE_ICON_SIZE} />}
              title={t("home.donor_modal.donor_selected_title")}
              subtitle={t("home.donor_modal.donor_selected_subtitle")}
              onPress={onDonorAlreadySelected}
              action="plan_home_action_row"
              elevated={false}
            />
            <ActionRowCard
              leading={
                <OrganicMiniIcon kind="find-care" size={CHOICE_ICON_SIZE} />
              }
              title={t("home.donor_modal.donor_need_help_title")}
              subtitle={t("home.donor_modal.donor_need_help_subtitle")}
              onPress={onNeedDonorHelp}
              action="plan_home_action_row"
              elevated={false}
            />
          </>
        ) : (
          <>
            <ActionRowCard
              leading={
                <OrganicMiniIcon kind="partner" size={CHOICE_ICON_SIZE} />
              }
              title={t("home.donor_modal.partner_title")}
              subtitle={t("home.donor_modal.partner_subtitle")}
              onPress={onChoosePartner}
              action="plan_home_action_row"
              elevated={false}
            />
            <ActionRowCard
              leading={<OrganicMiniIcon kind="donor" size={CHOICE_ICON_SIZE} />}
              title={t("home.donor_modal.donor_title")}
              subtitle={t("home.donor_modal.donor_subtitle")}
              onPress={onChooseDonor}
              action="plan_home_action_row"
              elevated={false}
            />
          </>
        )}
      </ScrollView>
    </Dialog>
  );
}

function CoverageStatusChoiceModal({
  visible,
  onClose,
  onHealthInsurance,
  onEmployerBenefits,
  onUnsure,
}: {
  visible: boolean;
  onClose: () => void;
  onHealthInsurance: () => void;
  onEmployerBenefits: () => void;
  onUnsure: () => void;
}) {
  const t = useTranslations();
  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      closeAccessibilityLabel={t("learn.article_actions.close_accessibility")}
      closeAction="close_sheet"
      backdropPressLabel="dismiss_sheet"
      testID="coverage-status-dialog"
    >
      <DialogHeader
        title={t("home.coverage_modal.title")}
        subtitle={t("home.coverage_modal.body")}
        style={{ marginBottom: 18 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flexShrink: 1 }}
        contentContainerStyle={{ gap: 10 }}
      >
        <ActionRowCard
          leading={<OrganicMiniIcon kind="costs" size={CHOICE_ICON_SIZE} />}
          title={t("home.coverage_modal.health_insurance_title")}
          subtitle={t("home.coverage_modal.health_insurance_subtitle")}
          onPress={onHealthInsurance}
          action="plan_home_action_row"
          elevated={false}
        />
        <ActionRowCard
          leading={<OrganicMiniIcon kind="concierge" size={CHOICE_ICON_SIZE} />}
          title={t("home.coverage_modal.employer_benefit_title")}
          subtitle={t("home.coverage_modal.employer_benefit_subtitle")}
          onPress={onEmployerBenefits}
          action="plan_home_action_row"
          elevated={false}
        />
        <ActionRowCard
          leading={<OrganicMiniIcon kind="find-care" size={CHOICE_ICON_SIZE} />}
          title={t("home.coverage_modal.unsure_title")}
          subtitle={t("home.coverage_modal.unsure_subtitle")}
          onPress={onUnsure}
          action="plan_home_action_row"
          elevated={false}
        />
      </ScrollView>
    </Dialog>
  );
}

/**
 * What a donor or carrier outside the US sees on Home: the helper paths are
 * US-only for now, so the page says so instead of offering steps.
 */
function HelperNotAvailableCard() {
  const t = useTranslations();
  return (
    <CardSurface style={{ padding: 16, marginTop: 12 }}>
      <EmptyState
        title={t("home.helper_not_available_title")}
        subtitle={t("home.helper_not_available_body")}
        style={{ paddingVertical: 8, paddingHorizontal: 0 }}
      />
    </CardSurface>
  );
}

export default function PlanHomeScreen() {
  const t = useTranslations();
  const { openDocumentUpload, pickAndOpenDocumentUpload } =
    useDocumentUploadController();
  const { openStartTreatment } = useStartTreatmentController();
  const { locale } = useLocale();
  const region = useRegion();
  const { data: patient, isLoading } = usePatient();
  // Read journey-defining state through the canonical projection rather than
  // deriving it here. A signed-out viewer has no patient to fetch for, so the
  // underlying query stays disabled and never settles; only the projection
  // distinguishes "no request will be made" from "a request is in flight".
  const isAuthReady = useIsAuthReady();
  const { patientId: apiPatientId } = useApiClient();
  const {
    canonical: canonicalExperienceContext,
    status: canonicalExperienceContextStatus,
  } = useAuthorizedExperienceProjection();
  const devSearch = getDevSearch();
  const { data: acceptedPlan } = useTreatmentPlan();
  /**
   * The cycles, and whether we know them yet.
   *
   * Gated on the same rule as the journey projection, and for the same reason:
   * `useCycles` is `enabled: !!patientId && !!tenantId`, so while the Stytch
   * session hydrates the query is *disabled* — `isLoading` false, `data`
   * undefined — indistinguishable by those two fields from a patient who has no
   * cycles. Reading it that way is what still flashed a Home after the
   * projection fix landed: the post-cycle variant is decided from
   * `homeCycleEnding.endedCycleType`, which comes from this list, so a Home
   * resolved before it arrived fell back to the stated goal and named the wrong
   * journey — "9 ovocytes" over a closed embryo cycle — until the list landed a
   * frame later and replaced it.
   *
   * An empty list is data: a patient with no cycles reaches her Home without
   * waiting.
   */
  const cyclesQuery = useCycles();
  const cycles = cyclesQuery.data;
  const cyclesStatus = resolveAuthorizedProjectionStatus({
    isAuthReady,
    hasPatient: Boolean(apiPatientId),
    isSettled: cyclesQuery.isSuccess || cyclesQuery.isError,
    hasError: cyclesQuery.isError,
    hasData: Boolean(cyclesQuery.data),
    isPaused: cyclesQuery.isPaused,
  });
  const isPartner = useMemo(() => isPartnerRole(patient), [patient]);
  /**
   * Whether the viewer is supporting someone else's cycle rather than living it.
   *
   * The relationship on the patient record decides it. `dev_viewerRole=partner`
   * previews the same voice on a dev fixture, the way the other `dev_` params
   * preview a layout: it changes whose words the page uses and which actions it
   * offers, never whose data it reads.
   */
  const viewerIsSupporting =
    isPartner ||
    (devSearch !== undefined &&
      readExperienceDevOverrides(devSearch).viewerRole === "partner");
  const persona = useUserPersona();
  const participantRole = useMemo(
    () =>
      resolveActiveViewerRole(
        devSearch !== undefined
          ? persona.treatmentGoal
          : (canonicalExperienceContext?.intent.goal ?? persona.treatmentGoal),
        participantRoleToViewerRole(patient?.participantRole),
      ),
    [
      canonicalExperienceContext?.intent.goal,
      devSearch,
      patient?.participantRole,
      persona.treatmentGoal,
    ],
  );
  const canonicalHelperRole = useMemo(() => {
    const role = resolveActiveViewerRole(
      canonicalExperienceContext?.intent.goal,
      participantRoleToViewerRole(
        canonicalExperienceContext?.viewer.participantRole,
      ),
    );
    return role === "donor" || role === "carrier" || role === "intended-parent"
      ? role
      : null;
  }, [
    canonicalExperienceContext?.intent.goal,
    canonicalExperienceContext?.viewer.participantRole,
  ]);
  const helperRole =
    DEV_HELPER_ROLE_OVERRIDE ??
    (devSearch !== undefined
      ? persona.journeyRole === "gestational-carrier"
        ? "carrier"
        : persona.journeyRole
      : canonicalExperienceContextStatus === "ready"
        ? canonicalHelperRole
        : null);
  const devOpenOverlay = getDevOpenOverlay();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const scrollViewRef = useRef<ScrollView>(null);
  const { showAuthGate } = useAuthGate();
  const queryClient = useQueryClient();
  const updatePatient = useUpdatePatient();
  const sendPartnerInvite = useSendPartnerInvite();
  const {
    scope: partnerSharingScope,
    isAvailable: isPartnerSharingScopeAvailable,
  } = usePartnerSharingScope();
  const transitionCycle = useActiveCycleWithPhases();
  const fetPlanForCycleQuery = useFETPlanForCycle({
    cycleId: transitionCycle.cycle?.id ?? null,
    enabled: transitionCycle.cycle?.cycleType === "fet",
  });
  const recordFETProcedureReport = useRecordFETProcedureReport();
  const reportFETTransferSelf = useReportFETTransferSelf();
  const { mutateAsync: createTransitionPhase } = useCreatePhase();
  const { mutateAsync: updateTransitionPhase } = useUpdatePhase();
  const { mutateAsync: createProcedureEvent } = useCreateCycleProcedureEvent();
  const { mutateAsync: updateAppointment } = useUpdateAppointment();
  const { data: patientNotes = [] } = usePatientNotes();
  const { mutateAsync: createPatientNote } = useCreatePatientNote();
  const { data: procedureEvents = [] } = useCycleProcedureEvents(
    transitionCycle.cycle?.id,
  );

  const pathway = useMemo(() => derivePathway(patient), [patient]);
  const plannedModality = useMemo(
    () =>
      acceptedPlan !== undefined && acceptedPlan !== null
        ? resolveAcceptedPlanModality(acceptedPlan)
        : resolvePlannedModality(patient?.fertilityIntent?.plannedModalities),
    [acceptedPlan, patient?.fertilityIntent?.plannedModalities],
  );
  const userAge = useMemo(
    () => calculateAge(patient?.dateOfBirth ?? undefined),
    [patient],
  );
  const { totalEggs, totalEmbryos } = useFrozenSpecimens();

  // Treatment dashboard data
  const treatmentData = useTreatmentDashboard();
  const { data: fetParticipantViews = [] } = useFETParticipantViews({
    enabled: isPartner && treatmentData.rawCycleType === "fet",
  });
  const fetParticipantView = fetParticipantViews[0];

  // Build today's concept agenda rows from the same adapter used by the
  // redesigned Care calendar, so home, Care, and cycle detail stay aligned.
  const todayIsoForSchedule = useMemo(() => getTodayString(), []);
  // Peek-ahead covers the next 3 days so the Today empty/done states can show
  // the real upcoming agenda instead of fixtures. One appointments query spans
  // today..+3 and one row-builder pass keys rows by date for both surfaces.
  const PEEK_AHEAD_DAY_COUNT = 3;
  const peekEndIsoForSchedule = useMemo(() => {
    const end = new Date(`${todayIsoForSchedule}T00:00:00`);
    end.setDate(end.getDate() + PEEK_AHEAD_DAY_COUNT);
    return toLocalDateString(end);
  }, [todayIsoForSchedule]);
  const { data: allPatientMeds } = usePatientMedications();
  const { data: scheduleMeds } = usePatientMedications({
    window: { from: todayIsoForSchedule, to: peekEndIsoForSchedule },
  });
  const { data: scheduleAppointments } = useAppointments({
    startDate: todayIsoForSchedule,
    endDate: peekEndIsoForSchedule,
    // Closed/post-cycle users can still have medication doses or follow-up
    // visits in the next few days. Home owns the shared near-term agenda, so
    // keep this query available outside an open cycle as well.
    enabled: true,
  });
  const devPostProcedurePrompt = getDevPostProcedurePrompt();
  const postProcedureCandidate =
    useMemo<PostProcedureTransitionCandidate | null>(
      () =>
        resolvePostProcedureTransitionCandidate({
          cycle: transitionCycle.cycle,
          phases: transitionCycle.phases,
          appointments: scheduleAppointments ?? [],
          today: todayIsoForSchedule,
          fixtureProcedure:
            DEV_ACTIVE_CYCLE_OVERRIDE && devPostProcedurePrompt
              ? devPostProcedurePrompt
              : null,
          procedureEvents,
        }),
      [
        devPostProcedurePrompt,
        scheduleAppointments,
        todayIsoForSchedule,
        transitionCycle.cycle,
        transitionCycle.phases,
        procedureEvents,
      ],
    );
  const postProcedureEndReached =
    !postProcedureCandidate ||
    isPostProcedureTransitionTimeReached({
      candidate: postProcedureCandidate,
      today: todayIsoForSchedule,
    });
  const [postProcedureDialogOpen, setPostProcedureDialogOpen] = useState(false);
  const [pregnancyTrackingDialogVisible, setPregnancyTrackingDialogVisible] =
    useState(false);
  const [postProcedureDismissedKey, setPostProcedureDismissedKey] = useState<
    string | null
  >(null);
  const [postProcedureSaving, setPostProcedureSaving] = useState(false);
  const [postProcedureError, setPostProcedureError] = useState<string | null>(
    null,
  );
  const fetProcedureReportRequestIdRef = useRef<string | null>(null);
  const [endCycleVisible, setEndCycleVisible] = useState(false);
  const [pendingCycleCancellation, setPendingCycleCancellation] = useState<{
    candidate: PostProcedureTransitionCandidate;
    notes: string;
  } | null>(null);

  const shouldAutoOpenPostProcedureDialog = useMemo(() => {
    if (showAuthGate) return false;
    if (!postProcedureCandidate) return false;
    if (!postProcedureEndReached) return false;
    if (postProcedureDismissedKey === postProcedureCandidate.key) return false;
    return shouldAutoPromptPostProcedureTransition({
      candidate: postProcedureCandidate,
      notes: patientNotes,
      today: todayIsoForSchedule,
    });
  }, [
    patientNotes,
    postProcedureCandidate,
    postProcedureDismissedKey,
    postProcedureEndReached,
    showAuthGate,
    todayIsoForSchedule,
  ]);

  useEffect(() => {
    if (!postProcedureCandidate) {
      setPostProcedureDialogOpen(false);
      return;
    }
    if (shouldAutoOpenPostProcedureDialog) {
      setPostProcedureDialogOpen(true);
    }
  }, [postProcedureCandidate, shouldAutoOpenPostProcedureDialog]);

  useEffect(() => {
    if (!pendingCycleCancellation) return;
    const timeoutId = setTimeout(() => setEndCycleVisible(true), 350);
    return () => clearTimeout(timeoutId);
  }, [pendingCycleCancellation]);

  const writePostProcedureTransitionRecord = useCallback(
    async (
      candidate: PostProcedureTransitionCandidate,
      status: PostProcedureTransitionStatus,
      procedureDate: string,
      notes: string,
      procedureTime: string | null = candidate.procedureTime,
    ) => {
      const procedureEventKind: PostProcedureEventKind | null =
        candidate.procedureEventKind ??
        (candidate.procedure === "insemination"
          ? "insemination"
          : candidate.procedure === "ovulation_window"
            ? "ovulation_window"
            : null);
      if (procedureEventKind) {
        const eventStatus =
          status === "pending" ? "pending_confirmation" : status;
        const isOccurred = eventStatus === "occurred";
        const scheduledDate =
          eventStatus === "rescheduled"
            ? procedureDate
            : candidate.procedureDate;
        const scheduledTime =
          eventStatus === "rescheduled"
            ? procedureTime
            : candidate.procedureTime;
        await createProcedureEvent({
          cycleId: candidate.cycleId,
          body: {
            kind: procedureEventKind,
            status: eventStatus,
            sourceKind: isOccurred ? "patient_confirmed" : "patient_reported",
            scheduledStartAt: procedureEventDateTime(
              scheduledDate,
              scheduledTime,
            ),
            ...(isOccurred
              ? {
                  occurredAt: procedureEventDateTime(
                    procedureDate,
                    procedureTime,
                  ),
                }
              : {}),
            ...(eventStatus === "rescheduled" && candidate.procedureEventId
              ? { supersedesEventId: candidate.procedureEventId }
              : {}),
            timeZone: localTimeZone(),
            notes: notes || null,
            clientRequestId: stableProcedureEventRequestId(
              `${candidate.key}:${eventStatus}:${procedureDate}:${procedureTime ?? ""}`,
            ),
          },
        });
        return;
      }

      await createPatientNote({
        cycleId: candidate.cycleId,
        date: procedureDate,
        text: serializePostProcedureTransitionRecord({
          key: candidate.key,
          cycleId: candidate.cycleId,
          procedure: candidate.procedure,
          status,
          procedureDate,
          notes,
          recordedAt: new Date().toISOString(),
        }),
      });
    },
    [createPatientNote, createProcedureEvent],
  );

  const recordFETPatientProcedureReport = useCallback(
    async ({
      candidate,
      status,
      procedureDate,
      scheduledAt,
      procedureTime,
      embryosTransferred,
      notes,
    }: {
      candidate: PostProcedureTransitionCandidate;
      status: "awaiting_report" | "rescheduled" | "cancelled";
      procedureDate: string | null;
      scheduledAt: string | null;
      procedureTime: string | null;
      embryosTransferred: number | null;
      notes: string;
    }) => {
      if (
        candidate.source === "fixture" ||
        candidate.procedure !== "embryo_transfer" ||
        transitionCycle.cycle?.cycleType !== "fet"
      ) {
        return;
      }

      // The plan is no longer read here. Which plan backs this cycle, and which
      // of its versions the cycle was snapshotted from, is the server's decision
      // inside the self-report transaction — resolving it client-side meant
      // guessing it from a possibly-stale cache and sending the plan's *current*
      // version rather than the snapshotted one.
      const clientRequestId =
        fetProcedureReportRequestIdRef.current ?? createIdempotencyKey();
      fetProcedureReportRequestIdRef.current = clientRequestId;

      // A dated report of a transfer that happened goes through the self-report
      // endpoint: it stands the plan chain up when no clinic filed one — a
      // patient cannot, acceptance needs a named embryo in cryo storage and a
      // consent reference she does not hold — and otherwise reuses the plan
      // already snapshotted into the cycle, under a lock and against the
      // snapshotted version rather than a guess made from a stale cache here.
      if (status === "awaiting_report" && procedureDate) {
        await reportFETTransferSelf.mutateAsync({
          cycleId: candidate.cycleId,
          actualDate: procedureDate,
          clientRequestId,
          ...(candidate.appointmentId
            ? { appointmentId: candidate.appointmentId }
            : {}),
          ...(scheduledAt ? { scheduledAt } : {}),
          ...(procedureTime
            ? { actualTime: procedureTime, timeZone: localTimeZone() }
            : {}),
          ...(embryosTransferred != null
            ? { actualTransferCount: embryosTransferred }
            : {}),
          ...(notes ? { notes } : {}),
        });
        return;
      }

      // A rescheduling or a cancellation says the transfer did *not* happen, so
      // there is nothing to stand a plan up for. These still require a plan the
      // clinic filed, exactly as before: refusing them outright made "it was
      // rescheduled" and "the cycle was cancelled" unanswerable on an FET cycle,
      // and left the dialog reappearing with a developer string in it.
      if (fetPlanForCycleQuery.isPending) {
        throw new Error("The accepted FET plan is still loading");
      }
      if (fetPlanForCycleQuery.error) {
        throw fetPlanForCycleQuery.error;
      }
      const plan = fetPlanForCycleQuery.data;
      const version = plan?.versions.find(
        (item) => item.version === plan.currentVersion,
      );
      if (!plan || !version) {
        throw new FETProcedureReportUnavailableError();
      }

      await recordFETProcedureReport.mutateAsync({
        planId: plan.id,
        planVersion: version.version,
        cycleId: candidate.cycleId,
        status,
        ...(candidate.appointmentId
          ? { appointmentId: candidate.appointmentId }
          : {}),
        ...(scheduledAt ? { scheduledAt } : {}),
        ...(embryosTransferred != null
          ? { actualTransferCount: embryosTransferred }
          : {}),
        ...(notes ? { notes } : {}),
        clientRequestId,
      });
    },
    [
      fetPlanForCycleQuery,
      recordFETProcedureReport,
      reportFETTransferSelf,
      transitionCycle.cycle?.cycleType,
    ],
  );

  const handleDismissPostProcedureDialog = useCallback(async () => {
    if (!postProcedureCandidate) return;
    try {
      setPostProcedureSaving(true);
      setPostProcedureError(null);
      await writePostProcedureTransitionRecord(
        postProcedureCandidate,
        "dismissed",
        postProcedureCandidate.procedureDate,
        "",
      );
      setPostProcedureDialogOpen(false);
      setPostProcedureDismissedKey(postProcedureCandidate.key);
    } catch {
      // Keep the dialog open. A dismissal without a server acknowledgement
      // would reappear unpredictably on another device and cannot be treated
      // as durable state.
      setPostProcedureError(
        t("homepage.review_concept.post_procedure.save_failed"),
      );
    } finally {
      setPostProcedureSaving(false);
    }
  }, [postProcedureCandidate, t, writePostProcedureTransitionRecord]);

  const handlePostProcedureTransition = useCallback(
    async (result: PostProcedureTransitionResult) => {
      const candidate = postProcedureCandidate;
      if (!candidate) return;

      setPostProcedureError(null);
      if (result.status !== "occurred") {
        if (
          result.status === "cancelled" &&
          result.cancellationDisposition === "cycle_cancelled"
        ) {
          try {
            setPostProcedureSaving(true);
            await recordFETPatientProcedureReport({
              candidate,
              status: "cancelled",
              procedureDate: null,
              scheduledAt: result.procedureDate,
              procedureTime: result.procedureTime,
              embryosTransferred: result.embryosTransferred,
              notes: result.notes,
            });
            fetProcedureReportRequestIdRef.current = null;
            setPostProcedureDialogOpen(false);
            // The check-in hands off to the end-cycle dialog, so it is handled
            // for this procedure. Without this the auto-open effect reopened it
            // as soon as the report write refreshed the candidate, and the
            // end-cycle dialog opened on top of it (#3669). Nothing is recorded
            // until the cycle actually ends, so abandoning the end-cycle dialog
            // leaves the check-in a tap away on the hand-off card.
            setPostProcedureDismissedKey(candidate.key);
            setPendingCycleCancellation({ candidate, notes: result.notes });
          } catch (error) {
            setPostProcedureError(
              error instanceof Error
                ? error.message
                : t("homepage.review_concept.post_procedure.save_failed"),
            );
          } finally {
            setPostProcedureSaving(false);
          }
          return;
        }
        if (
          result.status === "rescheduled" &&
          result.cancellationDisposition === "rescheduled"
        ) {
          try {
            setPostProcedureSaving(true);
            await recordFETPatientProcedureReport({
              candidate,
              status: "rescheduled",
              procedureDate: null,
              scheduledAt: result.procedureDate,
              procedureTime: result.procedureTime,
              embryosTransferred: result.embryosTransferred,
              notes: result.notes,
            });
            const scheduledTime =
              result.procedureTime ?? candidate.procedureTime;
            const scheduledEnd = rescheduledProcedureEnd({
              candidate,
              procedureDate: result.procedureDate,
              procedureTime: scheduledTime,
            });

            if (
              candidate.procedureEventKind ||
              candidate.procedure === "insemination" ||
              candidate.procedure === "ovulation_window"
            ) {
              await writePostProcedureTransitionRecord(
                candidate,
                "rescheduled",
                result.procedureDate,
                result.notes,
                scheduledTime,
              );
            }

            if (candidate.source === "appointment" && candidate.appointmentId) {
              await updateAppointment({
                appointmentId: candidate.appointmentId,
                startDate: result.procedureDate,
                ...(scheduledTime ? { startTime: scheduledTime } : {}),
                endDate: scheduledEnd.procedureEndDate,
                ...(scheduledEnd.procedureEndTime
                  ? { endTime: scheduledEnd.procedureEndTime }
                  : {}),
              });
            } else if (candidate.source === "phase") {
              const existingProcedurePhase = transitionCycle.phases.find(
                (phase) => phase.type === candidate.procedurePhaseType,
              );
              if (existingProcedurePhase?.id) {
                await updateTransitionPhase({
                  cycleId: candidate.cycleId,
                  phaseId: existingProcedurePhase.id,
                  body: {
                    startDate: result.procedureDate,
                    startTime: scheduledTime,
                  },
                });
              }
            }

            fetProcedureReportRequestIdRef.current = null;
            if (
              !candidate.procedureEventKind &&
              candidate.procedure !== "insemination" &&
              candidate.procedure !== "ovulation_window"
            ) {
              await writePostProcedureTransitionRecord(
                candidate,
                "rescheduled",
                result.procedureDate,
                result.notes,
              );
            }
            setPostProcedureDialogOpen(false);
            setPostProcedureDismissedKey(candidate.key);
            await queryClient.refetchQueries({
              queryKey: ["cycle-phases"],
              type: "all",
            });
            await queryClient.refetchQueries({
              queryKey: appointmentKeys.all,
              type: "all",
            });
          } catch (error) {
            setPostProcedureError(
              error instanceof Error
                ? error.message
                : t("homepage.review_concept.post_procedure.save_failed"),
            );
          } finally {
            setPostProcedureSaving(false);
          }
          return;
        }
        try {
          setPostProcedureSaving(true);
          await recordFETPatientProcedureReport({
            candidate,
            status:
              result.status === "cancelled" ? "cancelled" : "awaiting_report",
            procedureDate: null,
            scheduledAt: result.procedureDate,
            procedureTime: result.procedureTime,
            embryosTransferred: result.embryosTransferred,
            notes: result.notes,
          });
          await writePostProcedureTransitionRecord(
            candidate,
            result.status,
            result.procedureDate,
            result.notes,
          );
          fetProcedureReportRequestIdRef.current = null;
          setPostProcedureDialogOpen(false);
          setPostProcedureDismissedKey(candidate.key);
        } catch {
          setPostProcedureError(
            t("homepage.review_concept.post_procedure.save_failed"),
          );
        } finally {
          setPostProcedureSaving(false);
        }
        return;
      }

      try {
        setPostProcedureSaving(true);
        const procedureDate = result.procedureDate;
        await recordFETPatientProcedureReport({
          candidate,
          status: "awaiting_report",
          procedureDate,
          scheduledAt: null,
          procedureTime: result.procedureTime,
          embryosTransferred: result.embryosTransferred,
          notes: result.notes,
        });
        const endDate = nextDayForPostProcedure(procedureDate);
        const existingProcedurePhase = transitionCycle.phases.find(
          (phase) => phase.type === candidate.procedurePhaseType,
        );

        // A patient's report is the usable source in this patient-owned
        // product. Keep the typed FET procedure as awaiting_report so its
        // patient-reported provenance stays explicit, but do not make the
        // patient wait for a clinic connection or create generic legacy
        // transfer/luteal phases from this answer.
        if (
          candidate.source !== "fixture" &&
          candidate.procedure === "embryo_transfer" &&
          transitionCycle.cycle?.cycleType === "fet"
        ) {
          await writePostProcedureTransitionRecord(
            candidate,
            "occurred",
            procedureDate,
            result.notes,
          );
          fetProcedureReportRequestIdRef.current = null;
          setPostProcedureDialogOpen(false);
          setPostProcedureDismissedKey(candidate.key);
          return;
        }
        const fertilityMetrics =
          candidate.procedure === "embryo_transfer" &&
          result.embryosTransferred != null
            ? // A create carries values only: this call clears nothing, so
              // `buildFertilityMetrics` can emit no withdrawal here.
              buildFertilityMetrics(
                { embryosTransferred: result.embryosTransferred },
                existingProcedurePhase,
              )
                ?.filter(
                  (
                    metric,
                  ): metric is Extract<
                    FertilityMetricWrite,
                    { type: string }
                  > => !("op" in metric),
                )
                .map((metric) => ({ type: metric.type, value: metric.value }))
            : undefined;
        const inseminationInfo =
          candidate.procedure === "insemination"
            ? {
                ...(existingProcedurePhase?.inseminationInfo?.id
                  ? { id: existingProcedurePhase.inseminationInfo.id }
                  : {}),
                notes: result.notes || null,
              }
            : undefined;

        const hasTypedProcedureEvent =
          candidate.procedureEventKind ||
          candidate.procedure === "insemination" ||
          candidate.procedure === "ovulation_window";
        if (hasTypedProcedureEvent) {
          await writePostProcedureTransitionRecord(
            candidate,
            "occurred",
            procedureDate,
            result.notes,
            result.procedureTime,
          );
        }

        if (candidate.procedurePhaseType === "luteal_phase") {
          if (existingProcedurePhase?.id) {
            await updateTransitionPhase({
              cycleId: candidate.cycleId,
              phaseId: existingProcedurePhase.id,
              body: { startDate: procedureDate, endDate: null },
            });
          } else {
            await createTransitionPhase({
              cycleId: candidate.cycleId,
              body: { type: "luteal_phase", startDate: procedureDate },
            });
          }
        } else {
          const body = {
            startDate: procedureDate,
            startTime: result.procedureTime,
            endDate,
            ...(fertilityMetrics ? { fertilityMetrics } : {}),
            ...(inseminationInfo ? { inseminationInfo } : {}),
          };
          if (existingProcedurePhase?.id) {
            await updateTransitionPhase({
              cycleId: candidate.cycleId,
              phaseId: existingProcedurePhase.id,
              body,
            });
          } else {
            await createTransitionPhase({
              cycleId: candidate.cycleId,
              body: { type: candidate.procedurePhaseType, ...body },
            });
          }

          const existingLutealPhase = transitionCycle.phases.find(
            (phase) => phase.type === "luteal_phase",
          );
          if (existingLutealPhase?.id) {
            await updateTransitionPhase({
              cycleId: candidate.cycleId,
              phaseId: existingLutealPhase.id,
              body: { startDate: procedureDate, endDate: null },
            });
          } else {
            await createTransitionPhase({
              cycleId: candidate.cycleId,
              body: { type: "luteal_phase", startDate: procedureDate },
            });
          }
        }

        if (!hasTypedProcedureEvent) {
          await writePostProcedureTransitionRecord(
            candidate,
            "occurred",
            procedureDate,
            result.notes,
          );
        }
        setPostProcedureDialogOpen(false);
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
      } catch (error) {
        setPostProcedureError(
          error instanceof Error
            ? error.message
            : t("homepage.review_concept.post_procedure.save_failed"),
        );
      } finally {
        setPostProcedureSaving(false);
      }
    },
    [
      createTransitionPhase,
      postProcedureCandidate,
      queryClient,
      t,
      transitionCycle.cycle?.cycleType,
      transitionCycle.phases,
      updateAppointment,
      updateTransitionPhase,
      recordFETPatientProcedureReport,
      fetProcedureReportRequestIdRef,
      writePostProcedureTransitionRecord,
    ],
  );
  const scheduleRowsByDate = useMemo(() => {
    return buildTodayRowsByDate({
      medications: scheduleMeds ?? [],
      appointments: scheduleAppointments ?? [],
      testResults: [],
      symptoms: [],
      startDate: todayIsoForSchedule,
      endDate: peekEndIsoForSchedule,
      medicationDoseFallback: t("home.take_as_prescribed"),
      t,
      locale,
    });
  }, [
    scheduleMeds,
    scheduleAppointments,
    todayIsoForSchedule,
    peekEndIsoForSchedule,
    t,
    locale,
  ]);
  // Dev fixtures only: which stimulation-day rows the forced cycle may show.
  const devTreatment = devTodayTreatment({
    cycleType: transitionCycle.cycle?.cycleType ?? treatmentData.rawCycleType,
    currentPhase: treatmentData.rawCurrentPhase,
    hasOutcome: !!transitionCycle.cycle?.outcome,
  });
  const todayConceptRows = useMemo<TodayRow[]>(() => {
    const rows = [...(scheduleRowsByDate.get(todayIsoForSchedule) ?? [])];
    if (
      getDevPatientMedicationActionsEnabled() &&
      devTreatment.stimulationDose &&
      !rows.some((row) => row.kind === "medication")
    ) {
      rows.unshift({
        id: `dev-gonal-f-${todayIsoForSchedule}`,
        date: todayIsoForSchedule,
        time: "7:00 AM",
        title: "Gonal-F 225 IU",
        detail: "Stimulation dose",
        icon: "hormones",
        status: "med",
        kind: "medication",
        medicationName: "Gonal-F",
        medicationId: getDevPatientMedicationActionsMissingIdEnabled()
          ? undefined
          : "15640000-0000-4000-8000-000000000001",
        medicationDose: "225 IU",
        medicationFormat: "Subcutaneous pen",
        medicationForm: "pen",
        scheduledFor: `${todayIsoForSchedule}T07:00:00`,
        medicationAvailabilityStatus: "on-hand",
      });
    }
    if (
      treatmentData.isInTreatment &&
      (DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta" ||
        DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta_pending")
    ) {
      // Two-week-wait fixture: the day is a luteal-support med, not a stim
      // monitoring visit. Only inject when nothing real is already scheduled.
      if (DEV_ACTIVE_CYCLE_OVERRIDE && rows.length === 0) {
        rows.push({
          id: `dev-progesterone-${todayIsoForSchedule}`,
          date: todayIsoForSchedule,
          time: "8:00 PM",
          title: "Progesterone",
          detail: "Evening dose",
          icon: "hormones",
          status: "med",
          kind: "medication",
          medicationName: "Progesterone",
        });
      }
    } else if (
      treatmentData.isInTreatment &&
      DEV_ACTIVE_CYCLE_OVERRIDE &&
      DEV_ACTIVE_CYCLE_SCENARIO === "default" &&
      devTreatment.monitoringVisit &&
      !rows.some(
        (row) => row.kind === "appointment" || row.kind === "monitoring",
      )
    ) {
      rows.push({
        id: `dev-monitoring-${todayIsoForSchedule}`,
        appointmentId: `dev-monitoring-${todayIsoForSchedule}`,
        date: todayIsoForSchedule,
        time: "8:30 AM",
        title: "Monitoring appointment",
        detail: "Ultrasound + bloodwork",
        icon: "panel",
        kind: "monitoring",
        completable: true,
        procedureName: "Transvaginal ultrasound",
        appointmentResultType: "monitoring",
      });
    }
    return rows;
  }, [
    treatmentData.isInTreatment,
    scheduleRowsByDate,
    todayIsoForSchedule,
    devTreatment.stimulationDose,
    devTreatment.monitoringVisit,
  ]);
  const peekAheadDays = useMemo<PeekAheadDay[]>(() => {
    // Closed/post-cycle users can still have a medication dose or follow-up
    // appointment in the next few days. Keep this tied to the real schedule
    // query for every concept-home state; the empty result is meaningful and
    // must not be replaced by a stimulation fixture.
    const base = new Date(`${todayIsoForSchedule}T00:00:00`);
    return Array.from({ length: PEEK_AHEAD_DAY_COUNT }, (_, i) => {
      const day = new Date(base);
      day.setDate(day.getDate() + i + 1);
      const iso = toLocalDateString(day);
      return {
        label:
          i === 0
            ? t("homepage.schedule.tomorrow")
            : formatDate(day, locale, { weekday: "long" }),
        rows: scheduleRowsByDate.get(iso) ?? [],
        emptyTitle: t("homepage.schedule.nothing_scheduled"),
        emptyDetail: t("homepage.schedule.nothing_scheduled_detail"),
      };
    });
  }, [scheduleRowsByDate, todayIsoForSchedule, t, locale]);

  // Journey map modal (RoadmapModal). MVP scaffolding for the long-term
  // gamified 3D journey; opened from the map icon in the cycle hero.
  const [journeyMapVisible, setJourneyMapVisible] = useState(false);
  // Only while the map is open — see `useJourneySignals`.
  const journeySignals = useJourneySignals({
    cycle: selectJourneyCycle(cycles),
    cycles,
    hasGoal: !!(
      patient?.fertilityIntent?.goal ?? patient?.treatments?.[0]?.intent
    ),
    enabled: journeyMapVisible,
  });
  const [outcomeFunnelVisible, setOutcomeFunnelVisible] = useState(
    devOpenOverlay === "outcome-funnel",
  );
  const [insuranceModalVisible, setInsuranceModalVisible] = useState(false);
  const [coverageStatusModalVisible, setCoverageStatusModalVisible] =
    useState(false);
  const [intentPickerVisible, setIntentPickerVisible] = useState(false);
  const [partnerOrDonorVisible, setPartnerOrDonorVisible] = useState(false);
  const [partnerOrDonorStep, setPartnerOrDonorStep] =
    useState<PartnerOrDonorModalStep>("source");
  const [partnerInviteVisible, setPartnerInviteVisible] = useState(false);
  const [partnerInviteName, setPartnerInviteName] = useState("");
  const [partnerInvitePhone, setPartnerInvitePhone] = useState("");
  const [reviewMedicationSheet, setReviewMedicationSheet] =
    useState<HomepageMedicationFactSheetPayload | null>(null);
  const [editMed, setEditMed] = useState<EditMedicationData | null>(null);
  const [reviewProcedureSheet, setReviewProcedureSheet] =
    useState<HomepageProcedureFactSheetPayload | null>(null);
  const [reviewTestSheet, setReviewTestSheet] =
    useState<HomepageTestFactSheetPayload | null>(null);

  useEffect(() => {
    if (devOpenOverlay === "outcome-funnel") {
      setOutcomeFunnelVisible(true);
    }
  }, [devOpenOverlay]);

  const handleOpenReviewMedicationFactSheet = useCallback(
    (payload: HomepageMedicationFactSheetPayload) => {
      setReviewMedicationSheet(payload);
    },
    [],
  );
  const handleEditMedicationById = useCallback(
    (medicationId: string, fallbackName?: string) => {
      const pm = allPatientMeds?.find((p) => p.id === medicationId);
      if (!pm) return;
      const timeTaken = Array.isArray(pm.timeTaken)
        ? (pm.timeTaken as string[])
        : [];
      setEditMed({
        id: pm.id,
        name: pm.medication?.name ?? fallbackName ?? "",
        dose: pm.dose,
        doseUnit: pm.doseUnit,
        route: pm.route ?? null,
        form: pm.form ?? null,
        timeTaken,
        startDate: pm.startDate ?? undefined,
        endDate: pm.endDate ?? null,
        notes: pm.notes,
      });
    },
    [allPatientMeds],
  );
  const handleOpenReviewProcedureFactSheet = useCallback(
    (payload: HomepageProcedureFactSheetPayload) => {
      setReviewProcedureSheet(payload);
    },
    [],
  );
  const handleOpenReviewTestFactSheet = useCallback(
    (payload: HomepageTestFactSheetPayload) => {
      setReviewTestSheet(payload);
    },
    [],
  );
  const partnerInviteError =
    sendPartnerInvite.error instanceof Error
      ? sendPartnerInvite.error.message
      : sendPartnerInvite.error
        ? String(sendPartnerInvite.error)
        : null;
  const {
    primaryPolicy,
    hasInsurance,
    verificationStatus,
    fertilityCoverage,
    isLoading: coverageLoading,
  } = useCoverage();

  // Manual step completions (AsyncStorage-backed)
  const { markComplete: markStepComplete } = useManualStepCompletion();

  // Frozen inventory from the last closed embryo-freezing cycle. This feeds
  // the experience resolver, so it reads the persona's goal rather than the
  // resolved context. Only embryo preservation reports inventory here; egg and
  // sperm preservation reach their post-cycle Home through
  // `hasCompletedPreservationCycle` instead.
  const closedCycleStorage = useClosedCycleCryoStorage(
    persona.treatmentGoal === "embryo-freezing" ||
      persona.treatmentGoal === "embryo-preserve"
      ? "embryo_freezing"
      : null,
  );

  /**
   * Has she already carried a preservation cycle to its end?
   *
   * Read from the cycles themselves because no stage field answers it today:
   * nothing writes the onboarding stage's `post-treatment` value, and Home no
   * longer reads the legacy `homepageStage`. So a patient who finished egg
   * freezing kept the stage she started with, and Home showed her the page for
   * someone who has not begun.
   *
   * A cancelled ending does not count. `useDiscontinueCycle` closes a cycle the
   * same way `useCompleteCycle` does — both write `status: "closed"` — and only
   * the outcome tells them apart. A cycle stopped partway stored nothing and is
   * not a cycle she has been through.
   */
  const hasCompletedPreservationCycle = useMemo(
    () =>
      (cycles ?? []).some((cycle) => {
        if (!PRESERVATION_CYCLE_TYPES.has(cycle.cycleType)) return false;
        // `outcome` is optional on the collection type and null on the
        // resolver's; an absent outcome and a null one mean the same thing here.
        const { state } = resolvePostCycleLifecycle({
          ...cycle,
          outcome: cycle.outcome ?? null,
        });
        /**
         * Through the shared resolver rather than a second reading of "closed",
         * so this cannot drift from what the rest of the app calls a finished
         * preservation cycle — and so a cancellation is excluded for the right
         * reason. `useDiscontinueCycle` and `useCompleteCycle` both write
         * `status: "closed"`; only the outcome tells them apart.
         *
         * `closed_unspecified` counts too. For egg and embryo freezing the
         * resolver withholds `completed_preservation` until the final frozen
         * count exists, and those numbers can arrive days after the retrieval.
         * She has still been through the cycle in the meantime, and the home
         * that says so — with its results block honestly empty — is a better
         * answer for those days than the home of someone who has not started.
         */
        return (
          state === "completed_preservation" || state === "closed_unspecified"
        );
      }),
    [cycles],
  );
  /**
   * A sperm sample given and its analysis not back yet. The phase stays on
   * collection while the lab works, so this is read from the recorded steps.
   */
  const awaitingSemenAnalysis = useMemo(
    () =>
      isAwaitingSemenAnalysis({
        cycle: transitionCycle.cycle,
        phases: transitionCycle.phases,
        todayIso: todayIsoForSchedule,
      }),
    [transitionCycle.cycle, transitionCycle.phases, todayIsoForSchedule],
  );
  /**
   * Whether the sperm-freezing cycle Home is reporting on has its analysis
   * figures, read the way Home's semen card reads them. Until this was set,
   * nothing told the pending card a result had arrived on a closed cycle.
   */
  const hasSemenSummary = useMemo(
    () =>
      transitionCycle.cycle?.cycleType === "sperm_freezing" &&
      semenAnalysisResultsFromSpermiograms(
        transitionCycle.phases.flatMap((phase) => phase.spermiograms ?? []),
      ) !== null,
    [transitionCycle.cycle, transitionCycle.phases],
  );
  /** Whether that sample's collection is recorded; see `isSpermSampleCollected`. */
  const spermSampleCollected = useMemo(
    () =>
      isSpermSampleCollected({
        cycle: transitionCycle.cycle,
        phases: transitionCycle.phases,
        todayIso: todayIsoForSchedule,
      }),
    [transitionCycle.cycle, transitionCycle.phases, todayIsoForSchedule],
  );
  /**
   * The cycle this Home is reporting on, and how it ended.
   *
   * While a cycle is open there is no ending to name. Once it closes, the
   * outcome exists only on the cycle record: `useTreatmentDashboard` is backed
   * by `/active-cycle`, which returns nothing for a closed cycle, so the most
   * recently ended cycle has to be found in the collection instead.
   *
   * This deliberately does not depend on the resolved Home variant — the
   * variant is derived *from* this — so it cannot reuse
   * `projectionSourceCycleId` below.
   */
  const pregnancyCycleIds = useMemo(
    () => [
      ...new Set([
        ...(cycles ?? [])
          .filter(
            (cycle) =>
              cycle.status === "closed" && cycle.outcome?.type === "pregnancy",
          )
          .map((cycle) => cycle.id),
        ...(transitionCycle.cycle?.id ? [transitionCycle.cycle.id] : []),
      ]),
    ],
    [cycles, transitionCycle.cycle?.id],
  );
  const pregnancyReadings = useCyclePregnancyStatuses(pregnancyCycleIds);
  const pregnancyStandsByCycle = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(pregnancyReadings).map(([id, reading]) => [
          id,
          reading === "unknown"
            ? ("unknown" as const)
            : (reading?.status ?? null),
        ]),
      ),
    [pregnancyReadings],
  );
  const closedHomeSubject = useMemo(
    () => homeSubjectClosedCycle(cycles, Date.now(), pregnancyStandsByCycle),
    [cycles, pregnancyStandsByCycle],
  );
  const closedPregnancyReading = closedHomeSubject?.id
    ? (pregnancyReadings[closedHomeSubject.id] ?? null)
    : null;
  const homeCycleEnding = useMemo(() => {
    if (treatmentData.isInTreatment) {
      return {
        cycleType: treatmentData.rawCycleType,
        // No cycle has ended, which is a fact worth carrying rather than a
        // field worth omitting: `cycleType` above is the *open* cycle's type,
        // and a caller asking what ended must not be handed it.
        endedCycleType: null,
        cycleStatus: "open",
        outcome: null,
        cycleId: null,
        outcomeId: null,
        outcomeType: null,
        acknowledgedAt: null,
        endDate: null,
        pregnancyOutcome: null,
        miscarriageOutcome: null,
      };
    }

    const latestClosed = closedHomeSubject;
    const ending = readCycleEnding(latestClosed);

    return {
      // Prefer the ended cycle's own type: the dashboard's type describes the
      // active cycle, and there isn't one.
      cycleType: ending.endedCycleType ?? treatmentData.rawCycleType,
      cycleStatus: latestClosed ? "closed" : undefined,
      ...ending,
      patientPregnancyStands:
        closedPregnancyReading === "unknown"
          ? ("unknown" as const)
          : (closedPregnancyReading?.status ?? null),
    };
  }, [
    closedHomeSubject,
    closedPregnancyReading,
    treatmentData.isInTreatment,
    treatmentData.rawCycleType,
  ]);

  /**
   * Whether pregnancy follow-up has actually begun. Once it has, the ending is
   * no longer the newest thing in the patient's care and Home should not greet
   * them with it.
   */
  // Server-held, not device-held: how far along someone is must read the same
  // on every device and to everyone looking after her.
  const closingPregnancyFollowUp = useCyclePregnancyFollowUp(
    homeCycleEnding.outcomeType === "pregnancy"
      ? homeCycleEnding.cycleId
      : null,
  );
  const pregnancyFollowUpStatus = closingPregnancyFollowUp.data?.status ?? null;

  /**
   * Stable, deliberately.
   *
   * Passed inline, this changed identity on every render of this screen — and
   * acknowledging an outcome invalidates the cycle queries, so it re-rendered
   * immediately. Anything downstream that waits for a *layout* to settle by
   * depending on this callback was really firing on the next render instead,
   * which is the bug it was written to avoid.
   */
  const scrollHomeToY = useCallback((y: number) => {
    scrollViewRef.current?.scrollTo({ y, animated: true });
  }, []);

  /**
   * Whether this ending is recent enough to greet the patient with.
   *
   * The carousel is an arrival state, and an arrival has to be about something
   * that just happened. Two flows create cycles that are *born* closed and carry
   * an outcome — "Add past cycle" in `AllPastCyclesScreen` and the
   * fertility-treatments medical-history import — so without this, entering a
   * past IVF from years ago would greet the patient as though the result had just
   * come in, and a positive one would fire confetti.
   *
   * This is a recency bound, not the timer that was deliberately rejected for
   * retirement. Retirement asks "is this still the most relevant thing on your
   * Home", which only an event can answer. Arrival asks "did this just happen",
   * which is a question about time by definition. An ending older than the window
   * gets no arrival — and having never been acknowledged, no standing card
   * either, which is the right outcome for historical data.
   */
  const closingEndedRecently = useMemo(() => {
    return isCycleEndingRecent(homeCycleEnding.endDate);
  }, [homeCycleEnding]);

  const createTreatmentSnapshot = useCreateTreatmentSnapshot();

  /**
   * Whether the ending Home resolved is recent enough to drive the experience.
   *
   * An open cycle is always the present tense, so a closed ending only speaks
   * when there is no treatment underway.
   */
  const closedEndingDrivesExperience = useMemo(
    () =>
      !treatmentData.isInTreatment &&
      shouldUseCycleEndingForHomeExperience(homeCycleEnding),
    [homeCycleEnding, treatmentData.isInTreatment],
  );

  const homePregnancySubject = useMemo(() => {
    const cycle = closedEndingDrivesExperience
      ? closedHomeSubject
      : transitionCycle.cycle;
    return { cycleId: cycle?.id ?? null, outcome: cycle?.outcome ?? null };
  }, [closedEndingDrivesExperience, closedHomeSubject, transitionCycle.cycle]);
  const homePregnancyReading = homePregnancySubject.cycleId
    ? (pregnancyReadings[homePregnancySubject.cycleId] ?? null)
    : null;

  /**
   * The ending recorded on a cycle that is still open.
   *
   * Recording a result deliberately does not close the cycle, so an open cycle
   * can already carry the result Home has to report. `homeCycleEnding` stays
   * about the *closed* cycle — the arrival carousel, its recency window and the
   * acknowledged pause are all built on a cycle that ended, and an open cycle has
   * no end date to be recent against — so this is a separate reading whose only
   * job is to let the experience resolver reach the result.
   *
   * `/active-cycle` already returns the outcome on an open cycle, so this costs no
   * extra request: it reads the cycle the treatment dashboard is already showing.
   */
  const openCycleEnding = useMemo(
    () =>
      treatmentData.isInTreatment
        ? readCycleEnding(transitionCycle.cycle ?? null)
        : NO_CYCLE_ENDING,
    [transitionCycle.cycle, treatmentData.isInTreatment],
  );

  /**
   * Gated on a named result for the same reason the closed reading is: an outcome
   * row that names nothing records nothing, and a cycle open without a named
   * result is simply still in progress.
   */
  const openEndingDrivesExperience = Boolean(openCycleEnding.outcomeType);

  /**
   * The outcome record Home is actually reporting on — the closed cycle's when it
   * drives the experience, the open cycle's when the result arrived before the
   * cycle was closed, and nothing when neither names a result.
   *
   * Named because two things read it now: the experience resolver, which turns it
   * into a surface, and the closing card at the foot of the page, whose copy has
   * to know whether that result has since reached a birth.
   */
  const experienceOutcome = useMemo(
    () =>
      closedEndingDrivesExperience
        ? homeCycleEnding.outcome
        : openEndingDrivesExperience
          ? openCycleEnding.outcome
          : null,
    [
      closedEndingDrivesExperience,
      homeCycleEnding.outcome,
      openCycleEnding.outcome,
      openEndingDrivesExperience,
    ],
  );

  const homeExperience = useMemo(
    () =>
      resolveHomeExperience({
        patient,
        canonicalContext: canonicalExperienceContext ?? undefined,
        canonicalContextStatus: canonicalExperienceContextStatus,
        devSearch,
        persona: { ...persona, modality: plannedModality },
        pathway,
        helperRole,
        treatment: {
          isInTreatment: treatmentData.isInTreatment,
          cycleType: closedEndingDrivesExperience
            ? homeCycleEnding.cycleType
            : treatmentData.rawCycleType,
          currentPhase: treatmentData.rawCurrentPhase,
          cycleStatus: closedEndingDrivesExperience
            ? homeCycleEnding.cycleStatus
            : treatmentData.isInTreatment
              ? "open"
              : undefined,
          outcome: experienceOutcome,
          hasMonitoringSummary: !!treatmentData.monitoringSnapshot,
          hasCryoInventory: !!closedCycleStorage?.itemCount,
          hasCompletedPreservationCycle,
          awaitingSemenAnalysis,
          spermSampleCollected,
          hasSemenSummary,
        },
      }),
    [
      patient,
      canonicalExperienceContext,
      canonicalExperienceContextStatus,
      devSearch,
      persona,
      plannedModality,
      pathway,
      helperRole,
      treatmentData.isInTreatment,
      treatmentData.rawCurrentPhase,
      treatmentData.rawCycleType,
      treatmentData.monitoringSnapshot,
      closedCycleStorage?.itemCount,
      hasCompletedPreservationCycle,
      awaitingSemenAnalysis,
      spermSampleCollected,
      hasSemenSummary,
      homeCycleEnding,
      closedEndingDrivesExperience,
      experienceOutcome,
    ],
  );
  /**
   * The closing carousel is Home's arrival state for a cycle that has just
   * ended, and acknowledging it is what retires it.
   *
   * Gated on a real outcome row rather than on the cycle merely being closed: a
   * cycle closed with no ending recorded has nothing to say, and `acknowledgedAt`
   * is the server's own record that the patient has seen this. That makes the
   * dismissal durable without inventing local storage for it.
   */
  const closingContent = useMemo(() => {
    if (!homeCycleEnding.outcomeId) return null;

    // A pregnancy that has since reached a birth or a loss must not be announced
    // as a pregnancy. The carousel is resolved from `outcome.type`, which stays
    // `"pregnancy"` after the ending is recorded, so
    // without this a patient who has just logged a birth could be met with
    // confetti telling them they are pregnant.
    if (
      closedPregnancyReading === "unknown" ||
      hasConcludedPregnancy(homeCycleEnding)
    ) {
      return null;
    }

    const content = resolveCycleClosingContent({
      cycleType: homeCycleEnding.cycleType,
      pathway,
      gender: resolveHomepageJourneyGender(
        homeExperience.context.viewer.sexAtBirth,
      ),
      goal: resolveHomepageJourneyGoal(
        homeExperience.context.familyBuildingPlan.treatmentGoal,
      ),
      // The viewer's own relationship to the cycle is authoritative. Inferring
      // "supporting" from gender alone mislabels a female partner as the person
      // who carried the treatment.
      viewerIsSupporting,
      // `outcome.type` is already exactly `PostProcedureOutcomeResult`.
      outcomeResult: homeCycleEnding.outcomeType,
      outcomeResultStatus: homeCycleEnding.outcome?.resultStatus,
      outcomeVerificationStatus: homeCycleEnding.outcome?.verificationStatus,
    });

    // A null outcome type means two different things. On a preservation or
    // donation cycle it *is* the ending — `useCompleteCycle` closes those with a
    // deliberately null type and the display layer infers completion from the
    // cycle type. On a conceiving cycle it means nothing was ever recorded,
    // which is not an ending to announce.
    if (!homeCycleEnding.outcomeType && content.ending === "unresolved") {
      return null;
    }

    return content;
  }, [
    homeCycleEnding,
    closedPregnancyReading,
    pathway,
    homeExperience.context.viewer.sexAtBirth,
    homeExperience.context.familyBuildingPlan.treatmentGoal,
    viewerIsSupporting,
  ]);

  const closingRetired = useMemo(() => {
    if (!closingContent) return true;
    return resolveCycleClosingRetired({
      ending: closingContent.ending,
      family: closingContent.family,
      // A new cycle is the patient deciding what comes next, in their own time.
      hasOpenCycle: treatmentData.isInTreatment,
      pregnancyFollowUpStarted: pregnancyFollowUpStatus === "opted_in",
    });
  }, [closingContent, treatmentData.isInTreatment, pregnancyFollowUpStatus]);

  const showPreCycleEggFreezingConcept =
    homeExperience.surface === "precycle-egg-freezing";
  const showPreCycleEmbryoFreezingConcept =
    homeExperience.surface === "precycle-embryo-freezing";
  const showPreCycleSpermFreezingConcept =
    homeExperience.surface === "precycle-sperm-freezing";
  // Older records may have primaryIntent "donate" but no journeyRole. Derive
  // a render role so donors get the helper home instead of a blank screen:
  // the "egg-donor"/"sperm-donor" review-concept variants have no
  // concept/heroCopy entry, so falling through renders null (empty homepage).
  const effectiveHelperRole: "donor" | "carrier" | null =
    helperRole === "donor" || helperRole === "carrier"
      ? helperRole
      : homeExperience.context.familyBuildingPlan.primaryIntent === "donate"
        ? "donor"
        : null;
  const devSuggestedHomeSurface = getDevSuggestedHomeSurface();
  const devReviewConceptVariant = getHomepageReviewConceptVariant(
    devSuggestedHomeSurface,
  );
  const autoReviewConceptVariant = getAutoHomepageReviewConceptVariant({
    homeExperience,
    treatmentData,
    endedCycleType: homeCycleEnding.endedCycleType,
  });
  const reviewConceptVariant =
    devReviewConceptVariant ??
    autoReviewConceptVariant ??
    getFallbackHomepageReviewConceptVariant({
      homeExperience,
      treatmentData,
      rendersPreCycleSurface:
        showPreCycleEggFreezingConcept ||
        showPreCycleEmbryoFreezingConcept ||
        showPreCycleSpermFreezingConcept,
      hasHelperHome: effectiveHelperRole !== null,
    });
  const guidedPreCycleConceptVariant =
    getGuidedPreCycleConceptVariant(reviewConceptVariant);
  const scheduledPreCycleStartDate = useMemo(() => {
    if (!cycles) return null;
    const today = getTodayString();
    return (
      [...cycles]
        .filter(
          (cycle) =>
            cycle.status === "open" &&
            !!cycle.startDate &&
            cycle.startDate >= today,
        )
        .sort((a, b) => (a.startDate ?? "").localeCompare(b.startDate ?? ""))[0]
        ?.startDate ?? null
    );
  }, [cycles]);
  const reviewDashboardConceptVariant = guidedPreCycleConceptVariant
    ? null
    : reviewConceptVariant;
  const postProcedureOutcomeContentKey = getPostProcedureOutcomeContentKey(
    reviewDashboardConceptVariant,
  );
  const postProcedureOutcomeHandoffVariant =
    reviewDashboardConceptVariant?.endsWith("-positive")
      ? "positive"
      : reviewDashboardConceptVariant?.endsWith("-negative")
        ? "negative"
        : reviewDashboardConceptVariant?.endsWith("-unavailable")
          ? "unavailable"
          : "shared";
  const postProcedureOutcomeHandoffCopyKey = (
    field:
      | "title"
      | "body"
      | "cta"
      | "check_1"
      | "check_2"
      | "chat_prompt"
      | "secondary_cta",
  ) =>
    `homepage.review_concept.post_procedure_outcome.shared.handoff_${
      postProcedureOutcomeHandoffVariant === "shared"
        ? ""
        : `${postProcedureOutcomeHandoffVariant}_`
    }${field}`;

  /**
   * Whether the result this page reports has since reached a birth.
   *
   * The route cannot say: a birth keeps `type: "pregnancy"`, so it arrives on the
   * same `-positive` Home a beta drawn this morning does. See
   * `resolveRecordedBirth`, which the hero reads through the same rule.
   *
   * **The active cycle comes first, because that is the cycle the hero is about.**
   * Read from `experienceOutcome` alone, this disagreed with the hero: the hero
   * resolves its birth from the active cycle, while the experience outcome can be
   * a *different* record — a closed cycle's ending selected from the full cycle
   * list, or nothing at all when neither ending drives the surface. The result was
   * a page announcing a birth in its hero and still offering to set up pregnancy
   * follow-up at its foot. Whatever cycle the hero names, the closing card has to
   * be about the same one.
   */
  /**
   * Her own statement about the pregnancy on whichever cycle the hero names.
   *
   * The same cycle `recordedBirthOnHome` and the closing card read, for the same
   * reason: a birth or a loss is no longer written onto the result (#3492), so
   * without this record the page has nothing that says the pregnancy concluded.
   */
  const currentHomePregnancyStatus = homePregnancyReading;

  const recordedBirthOnHome = resolveRecordedBirth(
    homePregnancySubject.outcome,
    currentHomePregnancyStatus,
  );

  /**
   * Whether there is still an ending to state, and someone here who may state it.
   *
   * Read through the same rule the guidance and the hero use, from the same record
   * `recordedBirthOnHome` reads, so the closing card cannot disagree with either.
   * It answers three things at once: the pregnancy has not already concluded — a
   * birth *or* a loss both leave nothing to specify — and the viewer is the person
   * who carried the cycle, since recording an ending is not a partner's to do.
   */
  const standingPregnancyOnHome = shouldShowEarlyPregnancyGuidance({
    outcome: homePregnancySubject.outcome,
    patientPregnancyStands:
      currentHomePregnancyStatus === "unknown"
        ? "unknown"
        : (currentHomePregnancyStatus?.status ?? null),
    // Loading *or* failed: a read that did not come back is not her saying
    // nothing, and this content may only follow a pregnancy still standing.
    patientStatusPending: homePregnancyReading === "unknown",
    viewerIsSupporting,
  });

  /**
   * The closing card's copy, resolved once for whichever voice it is in.
   *
   * A birth takes its own set of keys rather than a fifth `handoff_*` variant: the
   * shared template's leaves are `title`, `body` and `cta`, and this copy belongs
   * beside the hero's own birth words, under `homepage.recorded_birth`.
   */
  const outcomeHandoffCopy = recordedBirthOnHome
    ? recordedBirthHandoffCopyKeys()
    : {
        titleKey: postProcedureOutcomeHandoffCopyKey("title"),
        bodyKey: postProcedureOutcomeHandoffCopyKey("body"),
        ctaKey: postProcedureOutcomeHandoffCopyKey("cta"),
        checklistKeys: [
          postProcedureOutcomeHandoffCopyKey("check_1"),
          postProcedureOutcomeHandoffCopyKey("check_2"),
        ] as const,
        chatPromptKey: postProcedureOutcomeHandoffCopyKey("chat_prompt"),
      };

  const isPostProcedureOutcomeHome = isPostProcedureOutcomeVariant(
    reviewDashboardConceptVariant,
  );

  const isCycleStatusReviewHome =
    reviewDashboardConceptVariant === "cycle-status-review";

  /**
   * Which of the three preservation journeys this post-cycle Home is about, so
   * the closing handoff card speaks about her material rather than in general —
   * and, being null everywhere else, what says "this is not a post-cycle Home"
   * to the blocks that ask.
   */
  const postCycleHandoffJourney = resolvePostCycleHandoffJourney(
    reviewDashboardConceptVariant,
  );
  const isPostCycleHome = postCycleHandoffJourney !== null;

  /**
   * Whether the hero already states this ending and carries its acknowledgement.
   *
   * The carousel's suppression was written as "not a post-result Home", which
   * reads as the same condition but is not: `cycle-status-review` — the cancelled
   * surface — is not one of the twenty modality routes, so a cancellation got
   * both, the carousel announcing the ending above a hero announcing it again in
   * different words. Naming what it actually asks is what keeps "one ending, one
   * voice" true as surfaces are added.
   *
   * Gated on the ending being a closed one, because that variant is reachable
   * with the cycle still open — and there the hero's own action is "Close this
   * cycle", not an acknowledgement.
   */
  const heroCarriesTheEnding =
    isPostProcedureOutcomeHome ||
    isPostCycleHome ||
    (isCycleStatusReviewHome && closedEndingDrivesExperience);

  const cycleStatusReviewKind: CycleStatusReviewKind | null =
    isCycleStatusReviewHome && homeExperience.scenario === "results_cancelled"
      ? "cancelled"
      : isCycleStatusReviewHome &&
          homeExperience.scenario === "results_converted"
        ? "converted"
        : null;
  const cycleStatusReviewCopyPrefix = cycleStatusReviewKind
    ? `homepage.review_concept.inline.cycle_status_review_${cycleStatusReviewKind}`
    : "homepage.review_concept.inline.cycle_status_review";
  // Two-week-wait home extras: an inline "how are you feeling" chat (logging the
  // experience via conversation instead of a symptom checklist) and a curated
  // reading list for the stage. Only mounted on the beta-wait / iui-wait /
  // oi-wait homes.
  const isTwwHome =
    reviewDashboardConceptVariant === "beta-wait" ||
    reviewDashboardConceptVariant === "iui-wait" ||
    reviewDashboardConceptVariant === "oi-wait";
  const { isRead } = useReadArticles();
  const waitCycleType =
    homeExperience.context.treatment.cycleType !== "unknown"
      ? homeExperience.context.treatment.cycleType
      : treatmentData.rawCycleType;
  const waitReadings = useMemo(
    () => getWaitStageReadings(t, waitCycleType),
    [t, waitCycleType],
  );
  /**
   * `oi-active` is deliberately absent from this list.
   *
   * The reading it would add is the curated active-stimulation one, and that
   * article speaks of IVF outcomes, egg freezing, OHSS and retrieval — none of
   * which describes an ovulation induction cycle. A reading list for these days
   * is content work, not a list to borrow. `iui-active` is absent for the same
   * reason.
   */
  const isActiveStimHome = Boolean(
    reviewDashboardConceptVariant &&
    (reviewDashboardConceptVariant === "ivf-active" ||
      reviewDashboardConceptVariant === "egg-freezing-active" ||
      reviewDashboardConceptVariant === "embryo-freezing-active"),
  );
  // An IVF Home past stimulation (retrieval booked, transfer day) is about
  // that step now, so stimulation's readings no longer belong on it.
  const cycleHeaderState = useCycleHeaderState();
  const isPastStimulationIvfHome =
    reviewDashboardConceptVariant === "ivf-active" &&
    ivfHomeStep(cycleHeaderState.kind) !== "stimulation";
  const stimReadings = useMemo(
    () =>
      isActiveStimHome && !isPastStimulationIvfHome
        ? getStimStageReadings(t)
        : [],
    [isActiveStimHome, isPastStimulationIvfHome, t],
  );
  const [waitChatExpanded, setWaitChatExpanded] = useState(false);
  const waitChatProgress = useSharedValue(0);
  const toggleWaitChat = useCallback(() => {
    const expanding = !waitChatExpanded;
    setWaitChatExpanded(expanding);
    waitChatProgress.value = withSpring(expanding ? 1 : 0, {
      damping: 28,
      stiffness: 180,
      mass: 0.8,
    });
  }, [waitChatExpanded, waitChatProgress]);

  /**
   * The closed cycle a post-cycle Home is about.
   *
   * Deliberately not `homeCycleEnding`: that reading expires with the thirty-day
   * arrival window, and this Home outlives it. The hero offers "review this
   * cycle" for as long as the Home stands, so the cycle it names has to be read
   * without a window — the latest closed cycle of the journey the variant is
   * already about.
   *
   * One reading for the whole post-cycle Home, so the hero's link and the
   * projection below it can never name two different cycles.
   */
  const postCycleSourceCycle = useMemo(() => {
    const cycleType =
      reviewDashboardConceptVariant === "egg-freezing-postcycle"
        ? "egg_freezing"
        : reviewDashboardConceptVariant === "embryo-freezing-postcycle"
          ? "embryo_freezing"
          : reviewDashboardConceptVariant === "sperm-freezing-postcycle"
            ? "sperm_freezing"
            : null;
    if (!cycleType) return null;

    return (
      [...(cycles ?? [])]
        .filter(
          (cycle) => cycle.status === "closed" && cycle.cycleType === cycleType,
        )
        .sort((a, b) => {
          const aDate = a.endDate ?? a.updatedAt ?? a.createdAt;
          const bDate = b.endDate ?? b.updatedAt ?? b.createdAt;
          return bDate.localeCompare(aDate);
        })[0] ?? null
    );
  }, [cycles, reviewDashboardConceptVariant]);

  /**
   * Only the two journeys that store specimens a projection can be run on.
   * Sperm freezing has its own cryosurvival estimate and no oocyte funnel, so
   * it shares the reading above but not this one.
   */
  const projectionSourceCycleId =
    reviewDashboardConceptVariant === "sperm-freezing-postcycle"
      ? null
      : (postCycleSourceCycle?.id ?? null);
  const startTreatmentModalDefaults = useMemo(() => {
    const treatmentGoal =
      homeExperience.context.familyBuildingPlan.treatmentGoal;
    const sexAtBirth = homeExperience.context.viewer.sexAtBirth;
    const gender: "male" | "female" | null =
      sexAtBirth === "AMAB" ? "male" : sexAtBirth === "AFAB" ? "female" : null;
    const presetCycleType: StartTreatmentCycleType | null =
      homeExperience.context.familyBuildingPlan.modality === "iui"
        ? "iui"
        : homeExperience.context.familyBuildingPlan.modality === "ivf"
          ? "ivf"
          : treatmentGoal === "egg-freezing"
            ? "egg_freezing"
            : treatmentGoal === "embryo-freezing"
              ? "embryo_freezing"
              : treatmentGoal === "sperm-freezing"
                ? "sperm_freezing"
                : null;

    return {
      goal:
        homeExperience.context.familyBuildingPlan.primaryIntent === "preserve"
          ? ("preserve" as const)
          : ("pregnancy" as const),
      gender,
      presetCycleType,
    };
  }, [homeExperience.context]);

  // Get cards and journey from the hook
  const contextModality = homeExperience.context.familyBuildingPlan.modality;
  const homepageModality = useMemo(() => {
    if (homeExperience.scenario === "trying_naturally") {
      return "natural" as const;
    }
    if (contextModality === "ivf" || contextModality === "iui") {
      return contextModality;
    }
    if (acceptedPlan !== undefined && acceptedPlan !== null) {
      return resolveAcceptedPlanModality(acceptedPlan);
    }
    const planned = patient?.fertilityIntent?.plannedModalities?.[0];
    if (planned === "ivf" || planned === "iui") return planned;
    return treatmentData.rawCycleType === "iui"
      ? "iui"
      : treatmentData.rawCycleType === "ivf" ||
          treatmentData.rawCycleType === "icsi"
        ? "ivf"
        : null;
  }, [
    contextModality,
    acceptedPlan,
    patient?.fertilityIntent?.plannedModalities,
    homeExperience.scenario,
    treatmentData.rawCycleType,
  ]);
  const { journey, progress, activeStep } = useHomepageCards(
    resolveHomepageJourneyGender(
      homeExperience.context.viewer.sexAtBirth,
      homeExperience.context.familyBuildingPlan.treatmentGoal,
      viewerIsSupporting,
    ),
    resolveHomepageJourneyGoal(
      homeExperience.context.familyBuildingPlan.treatmentGoal,
    ),
    pathway,
    homepageModality,
    participantRole === "unknown" ? null : participantRole,
  );
  const preCycleHeroActiveStep =
    (homeExperience.surface === "precycle-egg-freezing" &&
      journey.key === "egg-freezing-solo") ||
    (homeExperience.surface === "precycle-embryo-freezing" &&
      journey.key.startsWith("embryo-freezing-")) ||
    (homeExperience.surface === "precycle-sperm-freezing" &&
      journey.key === "sperm-freezing-solo")
      ? activeStep
      : null;

  // Intelligence layer
  const { interpretations: testInsights, queryState: testResultsQueryState } =
    useTestResultInsights();
  const showPendingSemenAnalysisCard = shouldShowPendingSemenAnalysisCard({
    homeExperience,
    treatmentData,
    testInsights,
  });
  const greeting = getGreeting(t, patient?.firstName ?? undefined);
  const handleOpenFullPlan = useCallback(() => {
    setJourneyMapVisible(true);
  }, []);

  const handleOpenIntentPicker = useCallback(() => {
    setIntentPickerVisible(true);
  }, []);

  const intentChoice = useSaveIntentChoice();
  const saveIntentChoice = intentChoice.save;
  const handleSelectIntent = useCallback(
    async (
      goal: string,
      stage: IntentPickerStage | undefined,
      primaryIntent?: IntentPickerPrimaryIntent,
    ) => {
      const saved = await saveIntentChoice(goal, stage, primaryIntent);
      if (saved) setIntentPickerVisible(false);
      return saved;
    },
    [saveIntentChoice],
  );

  const handleOpenPartnerOrDonorModal = useCallback(() => {
    setPartnerOrDonorStep("source");
    setPartnerOrDonorVisible(true);
  }, []);

  const handleClosePartnerOrDonorModal = useCallback(() => {
    setPartnerOrDonorVisible(false);
    setPartnerOrDonorStep("source");
  }, []);

  const handleChoosePartnerSource = useCallback(() => {
    updatePatient.mutate({ partnerChoice: "with-partner" });
    setPartnerOrDonorVisible(false);
    setPartnerOrDonorStep("source");
    setPartnerInviteVisible(true);
  }, [updatePatient]);

  const handleChooseDonorSource = useCallback(() => {
    setPartnerOrDonorStep("donor-status");
  }, []);

  const handleDonorAlreadySelected = useCallback(() => {
    updatePatient.mutate({ partnerChoice: "using-donor" });
    setPartnerOrDonorVisible(false);
    setPartnerOrDonorStep("source");
    navigation.dispatch(
      CommonActions.navigate({
        name: "Care",
        params: {
          screen: "Documents",
          params: { initialCategory: "legal_consent" },
        },
      }),
    );
  }, [navigation, updatePatient]);

  const handleNeedDonorHelp = useCallback(() => {
    updatePatient.mutate({ partnerChoice: "using-donor" });
    setPartnerOrDonorVisible(false);
    setPartnerOrDonorStep("source");
    navigation.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: { screen: "DonorRegistry" },
      }),
    );
  }, [navigation, updatePatient]);

  const handleSendPartnerInvite = useCallback(
    (name: string, phone: string, sharingCategories: SharingCategory[]) => {
      if (!partnerSharingScope) return;
      sendPartnerInvite.mutate({
        phone,
        firstName: name.trim() || undefined,
        sharingCategories,
        sharingScope: partnerSharingScope,
      });
    },
    [partnerSharingScope, sendPartnerInvite],
  );

  const handleChooseHealthInsurance = useCallback(() => {
    setCoverageStatusModalVisible(false);
    setInsuranceModalVisible(true);
  }, []);

  const handleChooseEmployerBenefits = useCallback(() => {
    setCoverageStatusModalVisible(false);
    navigation.dispatch(
      CommonActions.navigate({
        name: "Costs",
        params: { screen: "EmployerBenefits" },
      }),
    );
  }, [navigation]);

  const handleChooseCoverageUnsure = useCallback(() => {
    setCoverageStatusModalVisible(false);
    navigation.dispatch(
      CommonActions.navigate({
        name: "Costs",
        params: { screen: "CostsOverview" },
      }),
    );
  }, [navigation]);

  // Handle action presses — navigate to nested tab screens
  const handleAction = useCallback(
    (action: PlanAction) => {
      // Side-effect signals (no navigation)
      if (action.signal === "complete-step") {
        const stepId = action.params?.stepId;
        if (stepId) {
          void markStepComplete(
            getJourneyStepCompletionId(journey.key, stepId),
          );
        }
        return;
      }

      if (action.signal === "open-insurance-modal") {
        setInsuranceModalVisible(true);
        return;
      }

      if (action.signal === "open-coverage-status-modal") {
        setCoverageStatusModalVisible(true);
        return;
      }

      if (action.signal === "open-start-treatment-modal") {
        openStartTreatment({
          ...startTreatmentModalDefaults,
          presetCycleType:
            action.params?.presetCycleType ??
            startTreatmentModalDefaults.presetCycleType,
          source: "PlanHomeScreen",
        });
        return;
      }

      if (action.signal === "open-ai-chat") {
        const initialPrompt = action.params?.initialPrompt;
        navigation.dispatch(
          CommonActions.navigate({
            name: "Chat",
            params: {
              screen: "Assistant",
              params: initialPrompt
                ? { initialPrompt, autoSendInitialPrompt: true }
                : undefined,
            },
          }),
        );
        return;
      }

      if (action.signal === "open-partner-or-donor-modal") {
        handleOpenPartnerOrDonorModal();
        return;
      }

      if (action.signal === "close-recorded-cycle") {
        // Same window as the Care page's end-cycle CTA, so a cycle is ended
        // through one flow wherever the patient starts it. The modal preselects
        // the ending already recorded, so this confirms it rather than asking
        // for it again.
        //
        // Nothing is pending from this entry point: it carries no expectation of
        // a cancellation, so any stale one must not ride along.
        setPendingCycleCancellation(null);
        setEndCycleVisible(true);
        return;
      }

      if (action.signal === "open-recorded-cycle") {
        // The concept builders are static and hold no cycle, so a "review this
        // cycle" row built there cannot carry a cycleId — and a CycleDetailScreen
        // action without one is read below as a request to create a cycle. The
        // screen does know which cycle the result belongs to, so it resolves the
        // destination here: history for a closed cycle, the detail screen for an
        // open one, and the cycle list rather than a creation flow if neither.
        const closedCycleId = homeCycleEnding.cycleId;
        const openCycleId = treatmentData.cycleId;
        navigation.dispatch(
          CommonActions.navigate(
            closedCycleId
              ? {
                  name: "Care",
                  params: {
                    screen: "PastCycleDetail",
                    params: { cycleId: closedCycleId },
                  },
                }
              : openCycleId
                ? {
                    name: "Care",
                    params: {
                      screen: "CycleDetail",
                      params: {
                        cycleId: openCycleId,
                        // A caller can name the hub tab the result lives on, so
                        // "review the recorded result" lands on the result rather
                        // than on whichever step the cycle is currently at.
                        ...(action.params?.focus === "outcome"
                          ? { focus: "outcome" as const }
                          : {}),
                      },
                    },
                  }
                : { name: "Care", params: { screen: "AllPastCycles" } },
          ),
        );
        return;
      }

      if (action.signal === "open-intent-picker") {
        setIntentPickerVisible(true);
        return;
      }

      if (action.signal === "open-document-upload") {
        openDocumentUpload({
          defaultCategory: action.params?.initialCategory ?? "medical_records",
          initialFiles: action.documentFiles,
        });
        return;
      }

      // Cycle detail is an existing-cycle surface, and the concept builders are
      // static: an action meaning "this cycle" arrives without an id, so reading
      // every id-less one as a creation CTA sent "add monitoring data" to the
      // start-a-cycle flow. `resolveCycleDetailAction` owns that decision, where
      // it can be tested — the regression it guards, a creation CTA quietly
      // losing its route to start-treatment, is invisible to every other check.
      const cycleDetailTarget = resolveCycleDetailAction(action, {
        isInTreatment: treatmentData.isInTreatment,
        cycleId: treatmentData.cycleId,
      });

      if (cycleDetailTarget.kind === "open-cycle") {
        navigation.dispatch(
          CommonActions.navigate({
            name: "Care",
            params: {
              screen: "CycleDetail",
              params: {
                cycleId: cycleDetailTarget.cycleId,
                ...(cycleDetailTarget.focus
                  ? { focus: cycleDetailTarget.focus }
                  : {}),
              },
            },
          }),
        );
        return;
      }

      if (cycleDetailTarget.kind === "start-treatment") {
        openStartTreatment({
          ...startTreatmentModalDefaults,
          presetCycleType:
            action.params?.presetCycleType ??
            startTreatmentModalDefaults.presetCycleType,
          source: "PlanHomeScreen",
        });
        return;
      }

      if (!action.screen) return;

      if (
        action.screen === "DocumentsScreen" &&
        action.params?.openUpload === "true"
      ) {
        openDocumentUpload({
          defaultCategory: action.params.initialCategory ?? null,
        });
        return;
      }

      // Article deep links: screen=ArticleDetail, params.articleId
      if (action.screen === "ArticleDetail" && action.params?.articleId) {
        navigation.dispatch(
          CommonActions.navigate({
            name: "Learn",
            params: {
              screen: "ArticleDetail",
              params: { articleId: action.params.articleId, returnTab: "Home" },
            },
          }),
        );
        return;
      }

      // Cycle history deep link: screen=PastCycleDetail, params.cycleId.
      // Handled here rather than in SCREEN_MAP because `NestedRoute` admits only
      // screens whose params are optional, and this one requires a cycleId —
      // there is no such thing as "the" past cycle.
      if (action.screen === "PastCycleDetail" && action.params?.cycleId) {
        navigation.dispatch(
          CommonActions.navigate({
            name: "Care",
            params: {
              screen: "PastCycleDetail",
              params: { cycleId: action.params.cycleId },
            },
          }),
        );
        return;
      }

      const route = SCREEN_MAP[action.screen];
      if (!route) return;
      // Forward any params the action carried (e.g. cycleId for CycleDetail)
      const shouldReturnToHome =
        route.tab === "Access" && route.screen === "FindCare";
      const innerParams = action.params
        ? {
            ...action.params,
            ...(shouldReturnToHome && !action.params.returnTab
              ? { returnTab: "Home" }
              : {}),
          }
        : shouldReturnToHome
          ? { returnTab: "Home" }
          : undefined;
      navigation.dispatch(
        CommonActions.navigate({
          name: route.tab,
          params: innerParams
            ? { screen: route.screen, params: innerParams }
            : { screen: route.screen },
        }),
      );
    },
    [
      navigation,
      journey.key,
      homeCycleEnding.cycleId,
      treatmentData.cycleId,
      treatmentData.isInTreatment,
      markStepComplete,
      handleOpenPartnerOrDonorModal,
      openDocumentUpload,
      openStartTreatment,
      startTreatmentModalDefaults,
    ],
  );

  /**
   * Open the cycle the closing message is about. Shared by the carousel and the
   * standing card so both land on the same place, and defined here because it
   * needs `handleAction`.
   */
  const openEndedCycle = useCallback(() => {
    if (!homeCycleEnding.cycleId) return;
    handleAction({
      label: t("homepage.cycle_closing.ui.open_cycle"),
      screen: "PastCycleDetail",
      params: { cycleId: homeCycleEnding.cycleId },
    });
  }, [handleAction, homeCycleEnding.cycleId, t]);

  /**
   * The ending, for whichever hero is standing when a cycle closes.
   *
   * The closing carousel used to do this from a card above every Home surface,
   * and on a pre-cycle Home that card contradicted the hero beneath it: "you
   * went all the way through" over "0 of 3 steps complete, cycle not yet
   * chosen". One surface, one voice — so the hero takes the ending's own words.
   *
   * Only the first slide survives the move. The carousel's second and third
   * slides said what is true now and what could come next, which is what the
   * blocks under a *post-cycle* Home are for; a hero is one statement, not
   * three.
   *
   * `isCycleClosingArrival` is not optional here. It is the only thing that
   * reads `acknowledgedAt` on this path: `resolveCycleClosingRetired` returns
   * false unconditionally for preservation and donation, so without this an
   * ending she has already dismissed is announced again, every visit, for the
   * rest of the window.
   *
   * Null wherever a hero already carries the ending in its own words, so no
   * surface says it twice.
   */
  const heroCycleEnding = useMemo((): HeroCycleEnding | null => {
    if (
      !closingContent ||
      closingRetired ||
      heroCarriesTheEnding ||
      !closingEndedRecently ||
      !isCycleClosingArrival(homeCycleEnding) ||
      !homeCycleEnding.cycleId
    ) {
      return null;
    }
    const [firstSlide] = closingContent.slides;
    if (!firstSlide) return null;

    /**
     * No acknowledgement here, by design.
     *
     * This hero offered "Got it" until 2026-09-02, and dismissing genuinely gave
     * the hero its plan back — but a second button on an announcement she did not
     * ask for read as clutter, and the ending expires on its own. Since the most
     * recent cycle now decides the home, a patient who starts another cycle moves
     * straight onto it, which is the exit that actually matters.
     *
     * The consequence, stated rather than hidden: nothing writes
     * `outcome.acknowledgedAt` on *this* path any more, so the announcement
     * stands until the arrival window lapses. The post-result outcome stage
     * still acknowledges through `useAcknowledgePostProcedureOutcome`, which is
     * a different surface with a different press.
     *
     * `isCycleClosingArrival` is kept in the gate above so endings acknowledged
     * before this change stay dismissed.
     */
    return {
      title: t(firstSlide.titleKey),
      body: t(firstSlide.bodyKey),
      primary: {
        label: t("homepage.cycle_closing.ui.open_cycle"),
        onPress: openEndedCycle,
      },
    };
  }, [
    closingContent,
    closingEndedRecently,
    closingRetired,
    heroCarriesTheEnding,
    homeCycleEnding,
    openEndedCycle,
    t,
  ]);

  const partnerSourceOverlays = (
    <>
      <PartnerOrDonorModal
        visible={partnerOrDonorVisible}
        step={partnerOrDonorStep}
        onClose={handleClosePartnerOrDonorModal}
        onBack={() => setPartnerOrDonorStep("source")}
        onChoosePartner={handleChoosePartnerSource}
        onChooseDonor={handleChooseDonorSource}
        onDonorAlreadySelected={handleDonorAlreadySelected}
        onNeedDonorHelp={handleNeedDonorHelp}
      />
      <PartnerInviteModal
        visible={partnerInviteVisible}
        name={partnerInviteName}
        phone={partnerInvitePhone}
        onChangeName={setPartnerInviteName}
        onChangePhone={setPartnerInvitePhone}
        onClose={() => setPartnerInviteVisible(false)}
        onSend={handleSendPartnerInvite}
        isSending={sendPartnerInvite.isPending}
        sent={sendPartnerInvite.isSuccess}
        scopeReady={isPartnerSharingScopeAvailable}
        error={partnerInviteError}
        onDismissError={() => sendPartnerInvite.reset()}
      />
      <CoverageStatusChoiceModal
        visible={coverageStatusModalVisible}
        onClose={() => setCoverageStatusModalVisible(false)}
        onHealthInsurance={handleChooseHealthInsurance}
        onEmployerBenefits={handleChooseEmployerBenefits}
        onUnsure={handleChooseCoverageUnsure}
      />
    </>
  );

  /*
   * `devSearch` exempts the cycles wait, exactly as it exempts
   * `journeyResolutionStatus` (`resolveHomeExperience`: "Dev overrides
   * intentionally keep their existing legacy path. This makes route experiments
   * deterministic"). Without the same carve-out a dev-fixture Home renders the
   * loading layout until Stytch reports `isInitialized` — and those are the
   * routes the visual-evidence workflow captures, so a snapshot would come back
   * as the spinner instead of the fixture it asked for.
   */
  if (
    isLoading ||
    (devSearch === undefined && cyclesStatus === "loading") ||
    homeExperience.journeyResolutionStatus === "loading"
  ) {
    return (
      <GlassScreenLayout>
        <View
          testID="screen-loading"
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingState caption={t("home.loading")} />
        </View>
      </GlassScreenLayout>
    );
  }

  // FR users on a donation/surrogacy path see a coming-soon placeholder —
  // the helper paths (donor screening, surrogacy agreements) are
  // US-specific in this iteration. We gate on the primary journey rather than
  // a relationship record because onboarding has not created a real
  // co-fertility relationship yet.
  if (
    homeExperience.context.familyBuildingPlan.primaryIntent === "donate" &&
    region !== "US"
  ) {
    return (
      <GlassScreenLayout>
        <ScrollView
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <ScreenHeader title={greeting} />
          <HelperNotAvailableCard />
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  // Donors and carriers don't have their own fertility journey on Louise —
  // they're providing gametes / carrying for someone else. Suppress the
  // cycle / plan / journey content and surface only the role-adapted banner
  // and compensation placeholder. Baseline cards (Medical History, Documents,
  // Learn, Find Care) stay reachable via the bottom tab bar.
  // Exception: a donor who has started their retrieval/collection cycle drops
  // out of this helper hero and into the active-treatment home (resolved above
  // to an egg/sperm-freezing-active variant), since that cycle is identical to
  // the preservation pathway. A carrier leaves it only while she is in a
  // cycle Home has a layout for (the fallback above gives a carrier
  // mid-transfer the active-cycle layout for that cycle); otherwise she stays
  // here.
  const donorInActiveCycle =
    effectiveHelperRole === "donor" && treatmentData.isInTreatment;
  if (
    (effectiveHelperRole === "donor" ||
      (effectiveHelperRole === "carrier" &&
        !(treatmentData.isInTreatment && reviewConceptVariant))) &&
    !donorInActiveCycle
  ) {
    // FR users see a coming-soon placeholder — the helper paths (donor
    // screening, surrogacy agreements) are US-specific in this iteration.
    if (region !== "US") {
      return (
        <GlassScreenLayout>
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
          >
            <ScreenHeader title={greeting} />
            <HelperNotAvailableCard />
          </ScrollView>
        </GlassScreenLayout>
      );
    }

    if (effectiveHelperRole === "carrier") {
      const carrierScenario: CarrierJourneyScenario =
        homeExperience.scenario === "carrier_screening" ||
        homeExperience.scenario === "carrier_matched_legal"
          ? homeExperience.scenario
          : "carrier_application";

      return (
        <GlassScreenLayout>
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
          >
            <ScreenHeader title={greeting} />

            <CarrierGuidedHome
              scenario={carrierScenario}
              relationship={homeExperience.relationship}
              onAction={handleAction}
            />

            <CompensationPlaceholderCard
              role="carrier"
              onOpenMedicalHistory={() =>
                handleAction({
                  label: "Medical history",
                  screen: "MedicalHistory",
                })
              }
            />

            <HelperHomeActionCard
              imageSource={learnIcon}
              title={t("home.learn_card.title")}
              subtitle={t("home.learn_card.subtitle")}
              onPress={() =>
                handleAction({ label: "Learn", screen: "LearnScreen" })
              }
            />

            <View style={{ marginTop: 24 }}>
              <RecordsSection
                onOpenCategory={(category: RecordsCategory) =>
                  handleAction({
                    label: "Documents",
                    screen: "DocumentsScreen",
                    params: {
                      initialCategory:
                        RECORDS_CATEGORY_TO_DOC_CATEGORY[category],
                    },
                  })
                }
                onOpenImport={() =>
                  openDocumentUpload({
                    defaultCategory: null,
                  })
                }
                onViewAll={() =>
                  handleAction({
                    label: "Documents",
                    screen: "DocumentsScreen",
                  })
                }
              />
            </View>
          </ScrollView>
        </GlassScreenLayout>
      );
    }

    const upcoming = getHelperUpcomingSteps(t)[effectiveHelperRole];
    const totalSteps = upcoming.length + 1;
    return (
      <GlassScreenLayout>
        <ScrollView
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <ScreenHeader title={greeting} />

          <HelperJourneyHero
            role={effectiveHelperRole}
            onCompleteMedicalHistory={() =>
              handleAction({
                label: "Medical history",
                screen: "MedicalHistory",
              })
            }
          />

          <MedicalHistoryStepCard
            stepNumber={1}
            totalSteps={totalSteps}
            onAction={handleAction}
          />

          {upcoming.map((step, i) => (
            <HelperJourneyStep
              key={step.title}
              stepNumber={i + 2}
              totalSteps={totalSteps}
              title={step.title}
              subtitle={step.subtitle}
              imageSource={step.action?.imageSource}
              onPress={
                step.action
                  ? () =>
                      handleAction({
                        label: step.title,
                        screen: step.action!.screen,
                      })
                  : undefined
              }
            />
          ))}

          {effectiveHelperRole === "donor" && (
            <HelperHomeActionCard
              Icon={BookOpen}
              title="Full donor guide"
              subtitle="Screening, medication, retrieval, recovery, and your rights."
              onPress={() =>
                handleAction({
                  label: "Full donor guide",
                  screen: "ArticleDetail",
                  params: { articleId: EGG_DONATION_GUIDE_ARTICLE_ID },
                })
              }
            />
          )}

          <HelperHomeActionCard
            imageSource={learnIcon}
            title={t("home.learn_card.title")}
            subtitle={t("home.learn_card.subtitle")}
            onPress={() =>
              handleAction({ label: "Learn", screen: "LearnScreen" })
            }
          />

          <View style={{ marginTop: 24 }}>
            <RecordsSection
              onOpenCategory={(category: RecordsCategory) =>
                handleAction({
                  label: "Documents",
                  screen: "DocumentsScreen",
                  params: {
                    initialCategory: RECORDS_CATEGORY_TO_DOC_CATEGORY[category],
                  },
                })
              }
              onOpenImport={() =>
                openDocumentUpload({
                  defaultCategory: null,
                })
              }
              onViewAll={() =>
                handleAction({ label: "Documents", screen: "DocumentsScreen" })
              }
            />
          </View>
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  if (DEV_HOMEPAGE_ARCHIVE) {
    return (
      <GlassScreenLayout
        overlay={
          <>
            <RoadmapModal
              visible={journeyMapVisible}
              currentPhaseId={resolveRoadmapPhaseId(
                patient?.fertilityIntent?.stage,
                patient?.fertilityIntent?.goal ??
                  patient?.treatments?.[0]?.intent ??
                  null,
                cycles,
              )}
              journeyPath={resolveJourneyPath(
                cycles,
                patient?.fertilityIntent?.goal ??
                  patient?.treatments?.[0]?.intent ??
                  null,
              )}
              signals={journeySignals}
              onClose={() => setJourneyMapVisible(false)}
            />
            <InsuranceInfoModal
              visible={insuranceModalVisible}
              onClose={() => setInsuranceModalVisible(false)}
              primaryPolicy={primaryPolicy}
              hasInsurance={hasInsurance}
              verificationStatus={verificationStatus}
              fertilityCoverage={fertilityCoverage}
              isLoading={coverageLoading}
            />
            <IntentPickerModal
              visible={intentPickerVisible}
              currentIntent={patient?.fertilityIntent?.goal ?? null}
              onSelect={handleSelectIntent}
              onClose={() => setIntentPickerVisible(false)}
              isSaving={intentChoice.isSaving}
              saveError={
                intentChoice.hasError
                  ? t("you.intent_picker.journey_save_failed")
                  : null
              }
            />
            {partnerSourceOverlays}
          </>
        }
      >
        <ScrollView
          ref={scrollViewRef}
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <ScreenHeader
            title="Homepage archive"
            subtitle="Dev-only parking lot for components removed from active homepages."
          />
          <HomepageComponentArchive
            userAge={userAge}
            journey={journey}
            progress={progress}
            onAction={handleAction}
          />
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  return (
    <GlassScreenLayout
      overlay={
        <>
          <RoadmapModal
            visible={journeyMapVisible}
            currentPhaseId={resolveRoadmapPhaseId(
              patient?.fertilityIntent?.stage,
              patient?.fertilityIntent?.goal ??
                patient?.treatments?.[0]?.intent ??
                null,
              cycles,
            )}
            journeyPath={resolveJourneyPath(
              cycles,
              patient?.fertilityIntent?.goal ??
                patient?.treatments?.[0]?.intent ??
                null,
            )}
            signals={journeySignals}
            onClose={() => setJourneyMapVisible(false)}
          />
          <OutcomeFunnelModal
            visible={outcomeFunnelVisible}
            cycleType={treatmentData.cycleType}
            frozenEggs={
              totalEggs ||
              (closedCycleStorage?.itemType === "eggs"
                ? closedCycleStorage.itemCount
                : 0)
            }
            frozenEmbryos={
              totalEmbryos ||
              (closedCycleStorage?.itemType === "embryos"
                ? closedCycleStorage.itemCount
                : 0)
            }
            patientAge={userAge}
            onClose={() => setOutcomeFunnelVisible(false)}
          />
          <InsuranceInfoModal
            visible={insuranceModalVisible}
            onClose={() => setInsuranceModalVisible(false)}
            primaryPolicy={primaryPolicy}
            hasInsurance={hasInsurance}
            verificationStatus={verificationStatus}
            fertilityCoverage={fertilityCoverage}
            isLoading={coverageLoading}
          />
          <IntentPickerModal
            visible={intentPickerVisible}
            currentIntent={patient?.fertilityIntent?.goal ?? null}
            onSelect={handleSelectIntent}
            onClose={() => setIntentPickerVisible(false)}
            isSaving={intentChoice.isSaving}
            saveError={
              intentChoice.hasError
                ? t("you.intent_picker.journey_save_failed")
                : null
            }
          />
          <MedicationFactsheetSheet
            item={reviewMedicationSheet?.item ?? null}
            fallbackName={reviewMedicationSheet?.fallbackName}
            userContext={{
              ...reviewMedicationSheet?.userContext,
              source:
                allPatientMeds?.find(
                  (p) => p.id === reviewMedicationSheet?.medicationId,
                )?.source ?? null,
            }}
            medicationId={reviewMedicationSheet?.medicationId}
            onClose={() => setReviewMedicationSheet(null)}
            onLogDosePress={
              reviewMedicationSheet?.onLogDose
                ? () => {
                    const openDoseLogger = reviewMedicationSheet.onLogDose;
                    setReviewMedicationSheet(null);
                    openDoseLogger?.();
                  }
                : undefined
            }
            onEditPress={
              reviewMedicationSheet?.medicationId
                ? () => {
                    const id = reviewMedicationSheet.medicationId;
                    const name = reviewMedicationSheet.fallbackName;
                    setReviewMedicationSheet(null);
                    if (id) handleEditMedicationById(id, name);
                  }
                : undefined
            }
          />
          <AddMedicationModal
            visible={!!editMed}
            onClose={() => setEditMed(null)}
            cycleId=""
            editMedication={editMed}
          />
          <ProcedureDetailSheet
            item={reviewProcedureSheet?.item ?? null}
            appointment={
              reviewProcedureSheet
                ? {
                    type: "appointment",
                    id: reviewProcedureSheet.appointment.id,
                    appointmentId:
                      reviewProcedureSheet.appointment.appointmentId,
                    title: reviewProcedureSheet.appointment.title,
                    subtitle: reviewProcedureSheet.appointment.subtitle,
                    time: reviewProcedureSheet.appointment.time,
                    date: reviewProcedureSheet.appointment.date,
                    icon: CalendarClock,
                    factsheetId: reviewProcedureSheet.item.id,
                  }
                : undefined
            }
            onClose={() => setReviewProcedureSheet(null)}
          />
          <TestDetailSheet
            item={reviewTestSheet?.item ?? null}
            onClose={() => setReviewTestSheet(null)}
            returnTab="Home"
          />
          {partnerSourceOverlays}
        </>
      }
    >
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <ScreenHeader title={greeting} />

        {/* Role-adapted top banner — coming-soon support for donor/carrier/intended-parent;
            self-hides when helperRole is null or the feature flag is off */}
        <RoleAdaptedHomeBanner />

        {showPreCycleEggFreezingConcept && !reviewConceptVariant && (
          <PreCycleEggFreezingHome
            cycleEnding={heroCycleEnding}
            activeStep={preCycleHeroActiveStep}
            steps={journey.steps.map((step, i) => ({
              id: step.id,
              title: step.title,
              icon: step.icon,
              status: progress[i]?.status ?? "not-started",
            }))}
            completedSteps={
              progress.filter((step) => step.status === "complete").length
            }
            totalSteps={journey.steps.length}
            userAge={userAge}
            viewerIsSupporting={viewerIsSupporting}
            onAction={handleAction}
            onOpenPlan={handleOpenFullPlan}
            onEditPlan={handleOpenIntentPicker}
          />
        )}
        {showPreCycleEmbryoFreezingConcept && !reviewConceptVariant && (
          <PreCycleEmbryoFreezingHome
            cycleEnding={heroCycleEnding}
            activeStep={preCycleHeroActiveStep}
            steps={journey.steps.map((step, i) => ({
              id: step.id,
              title: step.title,
              icon: step.icon,
              status: progress[i]?.status ?? "not-started",
            }))}
            completedSteps={
              progress.filter((step) => step.status === "complete").length
            }
            totalSteps={journey.steps.length}
            userAge={userAge}
            viewerIsSupporting={viewerIsSupporting}
            onAction={handleAction}
            onOpenPlan={handleOpenFullPlan}
            onEditPlan={handleOpenIntentPicker}
          />
        )}
        {showPreCycleSpermFreezingConcept && !reviewConceptVariant && (
          <PreCycleSpermFreezingHome
            cycleEnding={heroCycleEnding}
            activeStep={preCycleHeroActiveStep}
            steps={journey.steps.map((step, i) => ({
              id: step.id,
              title: step.title,
              icon: step.icon,
              status: progress[i]?.status ?? "not-started",
            }))}
            completedSteps={
              progress.filter((step) => step.status === "complete").length
            }
            totalSteps={journey.steps.length}
            userAge={userAge}
            viewerIsSupporting={viewerIsSupporting}
            onAction={handleAction}
            onOpenPlan={handleOpenFullPlan}
            onEditPlan={handleOpenIntentPicker}
          />
        )}
        {guidedPreCycleConceptVariant && (
          <GuidedPreCycleConceptHome
            cycleEnding={heroCycleEnding}
            variant={guidedPreCycleConceptVariant}
            userAge={userAge}
            viewerSexAtBirth={homeExperience.context.viewer.sexAtBirth}
            viewerIsSupporting={viewerIsSupporting}
            onAction={handleAction}
            onOpenPlan={handleOpenFullPlan}
            onEditPlan={handleOpenIntentPicker}
            scheduledCycleStartDate={scheduledPreCycleStartDate}
          />
        )}
        {reviewDashboardConceptVariant && (
          <HomepageReviewConceptHome
            pregnancyStatusReading={homePregnancyReading}
            variant={reviewDashboardConceptVariant}
            cycleStatusReviewKind={cycleStatusReviewKind}
            testInsights={testInsights}
            testResultsQueryState={testResultsQueryState}
            renderSemenAnalysisPending={
              showPendingSemenAnalysisCard
                ? () => (
                    <TestResultInsightCardWithDetail
                      interpretation={buildPendingSemenAnalysisInsight(t)}
                      state={{
                        kind: "waiting",
                        expectedLabel: t(
                          "homepage.pending_semen_analysis.two_to_five_days",
                        ),
                      }}
                    />
                  )
                : undefined
            }
            projectionSourceCycleId={projectionSourceCycleId}
            // A closed cycle is absent from /active-cycle, so the hero needs the id
            // to reach its own outcome. Sourced from the same closed-cycle reading
            // that selected the variant, so the two can never disagree.
            outcomeCycleId={
              closedEndingDrivesExperience && isPostProcedureOutcomeHome
                ? homeCycleEnding.cycleId
                : null
            }
            // Same flag the closing carousel already uses, so the two surfaces
            // cannot end up addressing different people about one ending.
            // Only a destination, never a record to read — see the prop's own note.
            endedCycleId={
              closedEndingDrivesExperience ? homeCycleEnding.cycleId : null
            }
            // The post-cycle Home's own cycle, read without the arrival window
            // so its hero keeps offering that cycle long after the ending has
            // stopped driving the experience.
            postCycleCycleId={postCycleSourceCycle?.id ?? null}
            postCycleEndDate={postCycleSourceCycle?.endDate ?? null}
            // Null whenever this hero already states the ending itself, so the
            // two cannot both speak. See `heroCycleEnding`.
            cycleEnding={heroCycleEnding}
            viewerIsSupporting={viewerIsSupporting}
            onScrollToY={scrollHomeToY}
            onAction={handleAction}
            onOpenPregnancyTrackingSetup={() =>
              setPregnancyTrackingDialogVisible(true)
            }
            renderMonitoringCard={(widget) => (
              <OvarianStimulationMonitoringSection
                cycleId={transitionCycle.cycle?.id}
                cycleStartDate={transitionCycle.cycle?.startDate}
                phases={transitionCycle.phases}
                derivedStepProgress={[]}
                cycleType={transitionCycle.cycle?.cycleType}
                title={widget.title}
                titleMarginTop={0}
                // A lining preparation's story is one line growing toward the
                // transfer, so it opens on the chart.
                defaultView={
                  isLiningPreparationCycleType(transitionCycle.cycle?.cycleType)
                    ? "chart"
                    : "table"
                }
                onUploadReport={(
                  dateISO,
                  onMonitoringExtracted,
                  onDocumentSaved,
                ) => {
                  void pickAndOpenDocumentUpload({
                    defaultCategory: "medical_records",
                    defaultTags: [
                      "monitoring-report",
                      `monitoring-date:${dateISO}`,
                    ],
                    onMonitoringExtracted,
                    onSave: onDocumentSaved,
                  });
                }}
              />
            )}
            onOpenSchedule={(betaWaitAction) =>
              handleAction({
                label: t(
                  `homepage.review_concept.hero_dynamic.cta.${betaWaitAction === "schedule" ? "schedule_my_beta" : "record_my_beta"}`,
                ),
                screen: "CycleDetail",
                // A focus is required: without one, an id-less cycle-detail
                // action is read as a creation CTA. A pending beta opens the
                // prefilled appointment dialog; a scheduled beta opens the
                // outcome step so its result can be recorded.
                params: {
                  focus: betaWaitAction === "schedule" ? "beta" : "outcome",
                },
              })
            }
            onOpenJourneyMap={() => setJourneyMapVisible(true)}
            onOpenOutcomeFunnel={() => setOutcomeFunnelVisible(true)}
            onOpenMedicationFactSheet={handleOpenReviewMedicationFactSheet}
            onOpenProcedureFactSheet={handleOpenReviewProcedureFactSheet}
            onRequestDocumentAttachment={(row) =>
              pickAndOpenDocumentUpload({
                defaultCategory: "medical_records",
                defaultTags: [`today-action:${row.appointmentId ?? row.id}`],
              })
            }
            onOpenTestFactSheet={handleOpenReviewTestFactSheet}
            todayRows={todayConceptRows}
            peekAheadDays={peekAheadDays}
            afterTodaySlot={
              isTwwHome ? (
                <>
                  {/* Two-week wait: chat entry to log how they feel (better
                      than a symptom checklist, since Louise answers and
                      captures symptoms and worries), plus a curated reading
                      list for the stage. Sits above Testing / Good to know. */}
                  <AiAssistantCard
                    expanded={waitChatExpanded}
                    onToggle={toggleWaitChat}
                    progress={waitChatProgress}
                    returnTab="Home"
                    // A partner is asked about their own wait, not her body.
                    title={t(
                      viewerIsSupporting
                        ? "homepage.review_concept.wait_extras.supporting.how_are_you_doing"
                        : "homepage.review_concept.wait_extras.chat_title",
                    )}
                    subtitle={t(
                      viewerIsSupporting
                        ? "homepage.review_concept.wait_extras.supporting.tell_louise_whats_on_your_mind"
                        : "homepage.review_concept.wait_extras.chat_subtitle",
                    )}
                    emptyPrompt={t(
                      viewerIsSupporting
                        ? "homepage.review_concept.wait_extras.supporting.share_how_the_wait_feels_for_you"
                        : "homepage.review_concept.wait_extras.chat_empty_prompt",
                    )}
                    inputPlaceholder={t(
                      viewerIsSupporting
                        ? "homepage.review_concept.wait_extras.supporting.how_are_you_doing"
                        : "homepage.review_concept.wait_extras.chat_input_placeholder",
                    )}
                  />
                  {waitReadings.length > 0 && (
                    <ArticleSectionCard
                      label={t(
                        "homepage.review_concept.wait_extras.readings_label",
                      )}
                      articles={waitReadings}
                      isRead={isRead}
                      onOpenArticle={(article) =>
                        navigation.dispatch(
                          CommonActions.navigate({
                            name: "Learn",
                            params: {
                              screen: "ArticleDetail",
                              params: {
                                articleId: article.id,
                                returnTab: "Home",
                              },
                            },
                          }),
                        )
                      }
                    />
                  )}
                </>
              ) : undefined
            }
            endOfPageSlot={
              !isPostProcedureOutcomeHome && stimReadings.length === 1 ? (
                <ArticleBannerCard
                  label={t(
                    "homepage.review_concept.stim_extras.readings_label",
                  )}
                  article={stimReadings[0]!}
                  onPress={() =>
                    navigation.dispatch(
                      CommonActions.navigate({
                        name: "Learn",
                        params: {
                          screen: "ArticleDetail",
                          params: {
                            articleId: stimReadings[0]!.id,
                            returnTab: "Home",
                          },
                        },
                      }),
                    )
                  }
                />
              ) : !isPostProcedureOutcomeHome && stimReadings.length > 1 ? (
                <ArticleSectionCard
                  label={t(
                    "homepage.review_concept.stim_extras.readings_label",
                  )}
                  articles={stimReadings}
                  isRead={isRead}
                  onOpenArticle={(article) =>
                    navigation.dispatch(
                      CommonActions.navigate({
                        name: "Learn",
                        params: {
                          screen: "ArticleDetail",
                          params: {
                            articleId: article.id,
                            returnTab: "Home",
                          },
                        },
                      }),
                    )
                  }
                />
              ) : undefined
            }
          />
        )}

        {isPostProcedureOutcomeHome &&
        reviewDashboardConceptVariant?.endsWith("-positive") ? (
          <PregnancyTrackingSetupDialog
            visible={pregnancyTrackingDialogVisible}
            followUp={closingPregnancyFollowUp.data ?? null}
            onClose={() => setPregnancyTrackingDialogVisible(false)}
            onAction={handleAction}
          />
        ) : null}

        <PostProcedureTransitionDialog
          candidate={postProcedureCandidate}
          visible={postProcedureDialogOpen}
          isSaving={postProcedureSaving}
          error={postProcedureError}
          onClose={handleDismissPostProcedureDialog}
          onResolve={handlePostProcedureTransition}
        />

        <EndCycleModal
          visible={endCycleVisible}
          onClose={() => {
            setEndCycleVisible(false);
            // Dismissing without saving abandons the cancellation. Leaving it
            // pending meant a later close — from the hero, days afterwards —
            // inherited it and wrote a cancellation note for whatever ending the
            // patient chose then.
            setPendingCycleCancellation(null);
          }}
          cycleId={transitionCycle.cycle?.id}
          outcomeId={transitionCycle.cycle?.outcome?.id ?? null}
          preservationCycleType={transitionCycle.cycle?.cycleType}
          // The modal has to send back the outcome fields it does not own, or the
          // PATCH replaces them with nulls.
          existingOutcome={transitionCycle.cycle?.outcome ?? null}
          onBeforeSave={async () => {
            // `usePastCycleDetail` reads cached phase data for a closed cycle, so
            // a cycle closed without a snapshot loses it. Best effort: it must
            // not stop the patient ending their cycle.
            const id = transitionCycle.cycle?.id;
            if (!id) return;
            await createTreatmentSnapshot.mutateAsync(id).catch(() => {});
          }}
          transferDate={
            transitionCycle.phases.find(
              (phase) =>
                phase.type === "transfer" || phase.type === "insemination",
            )?.startDate ?? null
          }
          hasPregnancyOpportunity={hasConfirmedPregnancyProcedure(
            transitionCycle.phases,
          )}
          onSaved={(savedOutcomeType) => {
            const pending = pendingCycleCancellation;
            setEndCycleVisible(false);
            setPendingCycleCancellation(null);
            // Only record the cancellation when the patient actually cancelled.
            // This modal was opened expecting one, but all five endings are
            // offered once it is open — and it is now also reachable from the
            // hero, which carries no expectation at all. Writing the note
            // regardless put a "cancelled" entry in the record of a cycle the
            // patient had just closed as a pregnancy.
            if (pending && savedOutcomeType === "cancelled") {
              void writePostProcedureTransitionRecord(
                pending.candidate,
                "cancelled",
                pending.candidate.procedureDate,
                pending.notes,
              );
            }
          }}
          onOpenArticle={(articleId: string) => {
            const rootNav =
              navigation.getParent<NavigationProp<MainTabParamList>>();
            rootNav?.navigate("Learn", {
              screen: "ArticleDetail",
              params: { articleId, returnTab: "Home" },
            });
          }}
        />

        {/* A connected partner reads the same layout as the shared journey
            above. The consent-scoped FET tasks the server assigns them are
            theirs alone, so they follow it. */}
        {isPartner && fetParticipantView ? (
          <CardSurface style={{ padding: 20, marginTop: 16 }}>
            <FETParticipantHomeView view={fetParticipantView} />
          </CardSurface>
        ) : null}

        {/* Records — the canonical four-folder records surface. Outcome homes
            still need this handoff even when the active cycle has just closed;
            the result and procedure history remain part of the care story. */}
        {(treatmentData.isInTreatment ||
          isPostProcedureOutcomeHome ||
          isCycleStatusReviewHome ||
          isPostCycleHome) && (
          <RecordsSection
            topMargin={24}
            onOpenCategory={(category: RecordsCategory) =>
              handleAction({
                label: "Documents",
                screen: "DocumentsScreen",
                params: {
                  initialCategory: RECORDS_CATEGORY_TO_DOC_CATEGORY[category],
                },
              })
            }
            onOpenImport={() =>
              openDocumentUpload({
                defaultCategory: null,
              })
            }
            onViewAll={() =>
              handleAction({ label: "Documents", screen: "DocumentsScreen" })
            }
          />
        )}

        {postProcedureCandidate ? (
          <JourneyHandoffCard
            title={t(
              `homepage.review_concept.post_procedure.${
                postProcedureCandidate.procedure === "insemination"
                  ? "insemination"
                  : postProcedureCandidate.procedure === "ovulation_window"
                    ? "ovulation_window"
                    : "transfer"
              }.handoff_title`,
            )}
            body={t(
              `homepage.review_concept.post_procedure.${
                postProcedureCandidate.procedure === "insemination"
                  ? "insemination"
                  : postProcedureCandidate.procedure === "ovulation_window"
                    ? "ovulation_window"
                    : "transfer"
              }.handoff_body`,
            )}
            ctaLabel={t(
              `homepage.review_concept.post_procedure.${
                postProcedureCandidate.procedure === "insemination"
                  ? "insemination"
                  : postProcedureCandidate.procedure === "ovulation_window"
                    ? "ovulation_window"
                    : "transfer"
              }.handoff_cta`,
            )}
            checklist={[1, 2, 3].map((item) =>
              t(
                `homepage.review_concept.post_procedure.${
                  postProcedureCandidate.procedure === "insemination"
                    ? "insemination"
                    : postProcedureCandidate.procedure === "ovulation_window"
                      ? "ovulation_window"
                      : "transfer"
                }.handoff_check_${item}`,
              ),
            )}
            backgroundImage={handoffBackground}
            illustration={
              <OrganicExplanatoryIllustration kind="follicle-cohort" />
            }
            onPress={() => {
              setPostProcedureError(null);
              setPostProcedureDialogOpen(true);
            }}
          />
        ) : isPostProcedureOutcomeHome && postProcedureOutcomeContentKey ? (
          <JourneyHandoffCard
            title={t(outcomeHandoffCopy.titleKey)}
            body={t(outcomeHandoffCopy.bodyKey)}
            ctaLabel={t(outcomeHandoffCopy.ctaKey)}
            checklist={outcomeHandoffCopy.checklistKeys.map((key) => t(key))}
            backgroundImage={handoffBackground}
            illustration={
              <OrganicExplanatoryIllustration kind="follicle-cohort" />
            }
            secondaryAction={
              // Planning the next cycle is the patient's to start, so a partner
              // reads the same card without the button that would open it.
              postProcedureOutcomeHandoffVariant === "negative" &&
              !viewerIsSupporting
                ? {
                    label: t(
                      postProcedureOutcomeHandoffCopyKey("secondary_cta"),
                    ),
                    onPress: () =>
                      handleAction({
                        label: t(
                          postProcedureOutcomeHandoffCopyKey("secondary_cta"),
                        ),
                        signal: "open-start-treatment-modal",
                        params: {
                          presetCycleType: postProcedureOutcomeContentKey,
                        },
                      }),
                  }
                : // A recorded beta is a result, not a pregnancy she has told us
                  // about — so the closing card asked her to set up week-by-week
                  // follow-up for something the app is not yet allowed to count.
                  // What the moment actually wants is the ending stated: still
                  // going, ended, a birth. That is what unlocks the dated steps,
                  // and it lives on the cycle's outcome step.
                  //
                  // Follow-up is not taken away, only stopped being pushed here:
                  // it stays offered in "When you're ready" (`readyRows`), which a
                  // standing pregnancy now shows without waiting to be
                  // acknowledged.
                  //
                  // `standingPregnancyOnHome` is what makes the offer honest: an
                  // ending already recorded — a birth or a loss — leaves nothing to
                  // specify, and a partner is not the one who records it. And it
                  // needs an open cycle to land on: on a closed one the outcome
                  // step is not reachable, so promising it would be a dead end.
                  postProcedureOutcomeHandoffVariant === "positive" &&
                    standingPregnancyOnHome &&
                    treatmentData.cycleId
                  ? {
                      label: t(
                        "homepage.review_concept.post_procedure_outcome.shared.specify_this_cycles_outcome",
                      ),
                      onPress: () =>
                        handleAction({
                          label: t(
                            "homepage.review_concept.post_procedure_outcome.shared.specify_this_cycles_outcome",
                          ),
                          // Through the signal, never a hand-built
                          // `screen: "CycleDetail"`. An id-less cycle-detail action
                          // resolves to `start-treatment` whenever
                          // `isInTreatment` is false — which is exactly the state a
                          // closed cycle driving this surface puts us in — so
                          // "specify this cycle's outcome" would have opened the
                          // start-a-new-treatment modal. The signal resolves the
                          // cycle here and forwards the focus.
                          signal: "open-recorded-cycle",
                          params: { focus: "outcome" },
                        }),
                    }
                  : undefined
            }
            onPress={() =>
              handleAction({
                label: t(outcomeHandoffCopy.ctaKey),
                signal: "open-ai-chat",
                params: {
                  initialPrompt: t(outcomeHandoffCopy.chatPromptKey),
                },
              })
            }
          />
        ) : isCycleStatusReviewHome ? (
          <JourneyHandoffCard
            title={t(`${cycleStatusReviewCopyPrefix}_handoff_title`)}
            body={t(`${cycleStatusReviewCopyPrefix}_handoff_body`)}
            ctaLabel={t(`${cycleStatusReviewCopyPrefix}_handoff_cta`)}
            checklist={[1, 2].map((item) =>
              t(`${cycleStatusReviewCopyPrefix}_handoff_check_${item}`),
            )}
            backgroundImage={handoffBackground}
            illustration={
              <OrganicExplanatoryIllustration kind="follicle-cohort" />
            }
            onPress={() =>
              handleAction({
                label: t(`${cycleStatusReviewCopyPrefix}_handoff_cta`),
                signal: "open-ai-chat",
                params: {
                  initialPrompt: t(
                    `${cycleStatusReviewCopyPrefix}_question_prompt`,
                  ),
                },
              })
            }
          />
        ) : null}
      </ScrollView>
    </GlassScreenLayout>
  );
}
